import { connect } from './net.js';
import { createScene, W, H } from './scene.js';
import { makeLook, seedOf } from './visitors.js';
import { nextDoor, toyVisitorOf, GLITCH_DEV_NAMES } from '../game/glitch.js';
import { toyLevel, toySheet, TOY } from './toy.js';
import { createReactionPlayer } from './reactions-colour.js';
import { reactionFor } from './reactions.js';
import { currentLook, saveLook, currentDoor, saveDoor, lookChoice, nextChoice, choiceLabel, choiceSaves } from './look.js';
import { NIGHT, nightSheet } from './night.js';
import { createOneBitScene, W as W1, H as H1, FPS } from './scene-onebit.js';
import { oneBitScale, oneBitWidth, oneBitHeight, needsStrip } from './scale.js';
import { isPhone, viewSize, phoneSize, PHONE } from './phone.js';
import { demoScript } from './demo.js';
import { shiftTitle, shiftTitleShort, sidesLine, startOffer, roleToJoin, sideUsedUp, lastDoorNote } from '../game/shifts.js';
import { plainChat, PLAIN_NOTICE, isBlank, MAX_CHARS, LONG_NOTICE } from '../game/normalise.js';
import { mineIndex } from './mine.js';
import { roundSeconds } from './round-clock.js';
import { HALLOWEEN_LINES } from './halloween-lines.js';
import { createSound, revealEvent, muteLabel, specialFor, revealFileFor, cueSound } from './sound.js';
import { ledgerLabel } from './ledger-label.js';
import { createWords,SENTENCE_FONT } from './words.js';
import { tutorialDone, rememberTutorial, briefSeen, rememberBrief } from './tutorial.js';
import { INSPECTOR_BRIEF } from '../game/tutorial.js';
import { createTallies } from './tally.js';
import { OUTBREAK_NOTICE, raceLine } from '../game/envelope.js';
import { createEnvelope, mailSpot } from './envelope.js';
import { createMail } from './mail.js';
import { OFF_SHORT, OFF_TOOLTIP, OFF_TAB, OFF_GO } from '../game/teaser.js';
import { COPY_LABEL, COPIED_LABEL } from './coin.js';
import { onBlockLine } from '../game/onblock.js';
import { litOf, LIT_KNOCKS } from './neighbours-lit.js';
import { noteRound } from './report.js';
import { sumSteps, goldLine, sumEndMs, SUM_FIRST_MS, SUM_STEP_MS, createShiftTotal } from './sum.js';
import { landingPlan, callFrame, alertDelay, HOOKS, LUNGE_MS } from './call-lands.js';
import { phoneShake } from './red.js';
import { cardOf, cardText, CARD_EVENT } from './reveal-card.js';
import { cardPlan, outcomeOf, drawShareCard, drawBrightCard, brightFrameFile, brightSheetFile, recordDoor, xPostText, SIZES, BRIGHT_SIZES, SHARE_EVENT } from './share-card.js';
import { shareOut, shareDone, saveFile, cardFile, loadImage } from './share.js';
import { renderTonight, selectCopy, wantsTonight } from './tonight.js';
import { renderStory, wantsStory, STORY_LOOKS } from './story-view.js';
import { doorFooledLine } from '../game/tonight.js';
import { createBigKnockUi } from './bigknock-ui.js';
import { mountVisitor } from './bigknock-visitor.js';
import { windowOf } from '../game/bigknock-window.js';
import { BIGKNOCK_COPY } from '../game/bigknock-copy.js';
import { createMini } from './minigame.js';
import { DRAFTS } from '../game/drafts.js';
import { REGULARS } from '../game/regulars.js';
import { afterMini } from './mini-after.js';

const $ = id => document.getElementById(id);
// one-bit (default) or the old colour look; index.html already picked the stylesheet
const LOOK = document.documentElement.dataset.look || currentLook();
const ONE_BIT = LOOK === 'one-bit';
// sprint 33 item 3: the one-bit look's door in level 2 night colour (night.js), unless the viewer chose pure one bit
const FIXED_LOOK = document.documentElement.hasAttribute('data-look-fixed'); // the public game: night colour only (look.js fixLook)
const NIGHT_DOOR = ONE_BIT && (FIXED_LOOK || currentDoor() === 'night') && !!nightSheet(); // no sheet (offline): the door stays one bit
const CHOICE = lookChoice(LOOK, NIGHT_DOOR ? 'night' : 'one-bit');
if (ONE_BIT) document.documentElement.dataset.door = NIGHT_DOOR ? 'night' : 'one-bit';
const DEMO = new URLSearchParams(location.search).get('demo');
const DEMO_FEVER = !!DEMO && new URLSearchParams(location.search).get('fever') === 'on'; // &fever=on in a demo: a Full fever door, for screenshots
// Challenge a friend: /?c=<code> is a door someone shared. It starts once this browser is on the start screen (after the first minute).
let challengeIn = DEMO ? null : new URLSearchParams(location.search).get('c');
function tryChallenge() {
  if (!challengeIn || ui.phase !== 'start' || ui.fm || !ui.playerId) return;
  net.send({ t: 'challenge-start', code: challengeIn });
  challengeIn = null;
  try { history.replaceState(null, '', '/'); } catch { /* a sandboxed page keeps its address */ }
}
// ?demo=envelope&envt=<ms>: hold the envelope still that far into its reveal, for screenshots (demo pages only)
const ENV_AT = DEMO && /^\d+$/.test(new URLSearchParams(location.search).get('envt') ?? '') ? Number(new URLSearchParams(location.search).get('envt')) : undefined;
// ?demo=…&ct=<ms>: the call that far after it was made, held still (call-lands.js; 0 = the ka-thunk, 1000 = the land), for frame strips
const DEMO_CT = DEMO && /^\d+$/.test(new URLSearchParams(location.search).get('ct') ?? '') ? Number(new URLSearchParams(location.search).get('ct')) : null;
const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
// The shared count of safe homes (the hearts and "7/10 safe homes" in the menu bar) is the whole block's, so a visitor who has just arrived may find it already at 3/10.
// It is not shown until this browser has played a door of its own (sprint 31); the page remembers that it has.
const HOUSES_KEY = 'door.houses';
// the menu bar's count, said once when it first shows, and again each time one is lost
const HOUSES_LINE = "The count in the corner is the block's safe homes, out of 10. Each visitor anyone in this room lets in costs one home, until tonight's rule changes.";
const flatsLostLine = ({ left, total }) => `Someone let a visitor in. ${left} of ${total} homes are safe.`;
const housesSeen = () => !!DEMO || (() => { try { return localStorage.getItem(HOUSES_KEY) === '1'; } catch { return false; } })();
const ui = {
  houses: housesSeen(),
  room: 'practice', role: null, phase: 'start', // start | queued | round | reveal
  playerId: null, wallet: null, snapshot: null,
  round: null, visitor: null, revealKind: null, flicker: 1, outbreak: false, halloween: false,
  revealAt: null, revealInfo: null, // one-bit: when the verdict came and what it showed
  tallies: createTallies(), // the chalk tallies beside the door (roadmap 28)
  doors: { key: null, list: [] }, // the doors this night shift (this session in the free room), for the share card's icons and tally (recordDoor)
  sum: null, shiftTotal: createShiftTotal({ reduced: matchMedia('(prefers-reduced-motion: reduce)').matches }), // the sum written on the door after a verdict; the shift total in the corner
  briefOpen: false, briefJoin: null, briefShown: false, // the inspector brief (game/tutorial.js): open now, the role to join when it is closed, shown this visit
  fm: false, fmDone: false, // the first minute (game/first-minute.js): no menu, name or wallet until the second door's reveal is closed
  coach: null, coachOpen: null, asked: false, // the practice lesson in play (game/tutorial.js); phone: its text box over the chat, null = open until the first question
  tonight: null, // Tonight (game/tonight.js): { on, final (the last door's reveal is up), end (the server's result) } while a night is being played
  call: null, history: [], card: null, // every call lands (call-lands.js): the call in play; this door's own chat; the reveal card (reveal-card.js)
};
if (DEMO) window.doorUi = ui; // demos only: the shoot scripts read ui.share to draw each end screen's own card

// The sound hooks for the call (call-lands.js HOOKS, listed in studio/sprints/27/item3-design.md): door-engineer plays the
// new stings here, through the existing sound settings. Each is also a window CustomEvent of the same name.
function cue(hook, detail = {}) { dispatchEvent(new CustomEvent(hook, { detail })); }

// launch mode (roadmap 43): the server left the night shift teaser in the page, so the night shift is off
const NIGHT_OFF = !!$('night-teaser');
if (NIGHT_OFF) { const tab = document.querySelector('.rooms [data-room="night"]'); tab.dataset.off = '1'; tab.title = OFF_TOOLTIP; tab.setAttribute('aria-label', OFF_TAB); }

const QUESTIONS = {
  colour: ['what colour is your door?', 'what colour is your coat?'],
  numbers: ['which flat number are you?', 'how many floors up?'],
  questions: ['anything you want to ask me?', 'who are you?'],
  days: ['what day is it?', 'when did you move in?'],
  self: ['tell me about yourself', 'who are you?'],
  cold: ['how is it out there?', 'why so late?'],
  contractions: ["you don't live here, do you?", 'why are you here?'],
  laugh: ['tell me a joke', 'why so late?'],
  animals: ['got any pets?', 'what was that noise?'],
  lowercase: ['what is your name?', 'who sent you?'],
};

// The lessons and the first minute suggest tonight's own questions, to teach the method. A normal door suggests two
// everyday questions from every rule's list, picked once per door, so the player still has to find tonight's question.
const EVERYDAY = [...new Set(Object.values(QUESTIONS).flat())];
const chipsFor = tell => {
  if (ui.coach || ui.fm) return QUESTIONS[tell] ?? [];
  if (!ui.chipPick) {
    const pool = [...EVERYDAY];
    ui.chipPick = [0, 1].map(() => pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
  }
  return ui.chipPick;
};

// ---------- sound (generated in code; silent until the first click or key press, see sound.js) ----------
const sound = createSound({ isRound: () => ui.phase === 'round', isHalloween: () => !!ui.halloween });
let stung = false; // set while the land plays its reveal sound: a sting takes the place of right and wrong, alien and infected still play
const sfx = e => (stung && (e === 'right' || e === 'wrong') ? null : sound.play(e, ui.halloween));
for (const hook of Object.values(HOOKS)) addEventListener(hook, e => cueSound(sound, hook, e.detail, ui.halloween)); // the ka-thunk and the stings
const envelope = createEnvelope({ root: $('envelope'), sfx });
// the slide-under-the-door envelope after a right call (mail.js, mail-art.js): sound goes through the same settings as every other sound
const mail = createMail({ root: $('mail'), sfx: e => sound.play(e, ui.halloween), onOpen: o => { net.send({ t: 'mail-open', skipped: o.skipped }); if (ui.mailTier === 'jackpot' && !$('share').hidden) $('share').focus(); } }); // the gilded result puts the Share button under the finger
const MAIL_AT = DEMO && /^[0-9]+$/.test(new URLSearchParams(location.search).get('mt') ?? '') ? Number(new URLSearchParams(location.search).get('mt')) : undefined; // ?demo=mail-*&mt=<ms>: hold the peephole still that far in
// the live line (roadmap 29): night room only, the server's board and pool as they stand
// (on a phone the play area makes room for it, so a change of shown/hidden or of height refits the page)
// Sprint 31 fix pass (QA B4): the play area never gets shorter than PHONE.minPlayH, so on a short phone the fixed line (three rows) lies over the bottom of it,
// over the reveal card's Next door button. While a reveal is up and the line would not fit under the play area, it waits (the verdict has the screen).
const raceCoversReveal = () => PHONE_ON && ui.phase === 'reveal' && !!ui.raceH && Math.floor((viewSize()[1] - $('strip').offsetHeight - ui.raceH) / PHONE.scale) < PHONE.minPlayH;
const showRace = () => {
  const el = $('race'); if (!el.hidden && el.offsetHeight) ui.raceH = el.offsetHeight;
  const t = ui.room === 'night' && !raceCoversReveal() ? raceLine(ui.snapshot?.race) : '', was = [el.hidden, el.offsetHeight];
  if (el.textContent !== t) el.textContent = t;
  el.hidden = !t;
  if (PHONE_ON && (was[0] !== el.hidden || was[1] !== el.offsetHeight)) fit();
};
// the line wraps to its real height once its font is in: measured again then, so the reveal check above sees what it is now
if (typeof ResizeObserver === 'function') new ResizeObserver(() => { if (PHONE_ON && !$('race').hidden) showRace(); }).observe($('race'));
addEventListener('keydown', e => { if (e.key === 'Escape' && envelope.open) { e.preventDefault(); $('envelope').querySelector('.env-close')?.click(); } });
const unlockSound = () => { sound.unlock(); removeEventListener('pointerdown', unlockSound); removeEventListener('keydown', unlockSound); };
addEventListener('pointerdown', unlockSound); addEventListener('keydown', unlockSound);

// ---------- networking ----------
// ?demo=… replays fixed messages (demo.js) for screenshots; it never opens a connection or sends anything
const net = DEMO ? { send() {} } : connect(onMsg);
// The mini-game after a special guest's reveal and its sum (sprint 35 item 1, client/minigame.js): the server offers it, the sum finishes, the window opens.
const mini = createMini({ doc: document, root: $('mini'), send: m => net.send(m), reduced: reducedMotion, onDone: m => onMiniDone(m) });

// A name the server keeps for this browser or wallet (game/names.js) fills the field; the field may stay empty (Guest).
function keepName(m) { if (m.named && m.name) { ui.savedName = m.name; $('name').value = m.name; } else if (m.name) $('name').placeholder = m.name; } // an unnamed player sees their default name in the field
// A session in play: a door being played, a knock waiting, or a story or Tonight night between its doors. Opening a page over it
// The glitch (game/glitch.js, client/toy.js): at each new door toy town starts, carries on or ends. Never on a lesson's door
// or the Big Knock's. The dev names see it flip on every door.
const glitch = { toy: false, left: 0, since: -Infinity, until: -Infinity, variant: 0, talkUntil: 0 };
const glitchDev = () => GLITCH_DEV_NAMES.includes((ui.savedName ?? $('name')?.value ?? '').trim().toLowerCase());
function glitchDoor(m) {
  const t = performance.now(), n = m.coach || m.bigknock ? { toy: false, left: 0 } : nextDoor(glitch, seedOf(m.id), { dev: glitchDev() });
  if (n.toy && !glitch.toy) { Object.assign(glitch, { since: t, until: Infinity, variant: toyVisitorOf(seedOf(m.id)) }); toySheet(); sound.toy(true); sound.glitch(); }
  else if (!n.toy && glitch.toy) glitchEnd();
  glitch.toy = n.toy; glitch.left = n.left;
}
function glitchEnd() {
  if (!glitch.toy) return;
  Object.assign(glitch, { toy: false, left: 0, until: performance.now() }); sound.toy(false); sound.glitch();
}
// asks first ("Step away from the door?"); leaving ends the door uncounted, stops its sound, and keeps a story's or a night's place.
const inSession = () => ['round', 'queued', 'wait'].includes(ui.phase) || !!(ui.story?.on || ui.tonight?.on) && ui.phase !== 'start'; // a decided door (reacting, reveal) is already counted
function leaveSession() {
  net.send({ t: 'leave' });
  if (ui.story?.on) pressRoom('practice');
  sound.stop('hum'); sound.quiet(); glitchEnd();
  ui.story = null; ui.tonight = null; ui.call = null;
  $('tonight').hidden = true;
  if (ui.coach) backToRoom();
  setPhase('start');
  if (ui.snapshot?.tell) $('bulletin').textContent = ui.snapshot.tell.bulletin;
}
let afterLeave = null;
function askLeave(then) {
  afterLeave = then;
  const run = ui.story?.on ? ' Your place in the story is kept.' : ui.tonight?.on ? " Tonight's doors you played are kept." : '';
  $('leave-p').firstChild.textContent = ui.phase === 'round' ? `Someone is still knocking. If you leave now, this door ends and nothing is counted.${run}`
    : ui.phase === 'queued' || ui.phase === 'wait' ? `You are waiting for the next door. If you leave now, nothing is counted.${run}`
    : `Your night is not over yet.${run || ' You can start again from the door.'}`;
  $('leave').hidden = false;
  $('leave-stay').focus();
}
$('leave-go').onclick = () => { $('leave').hidden = true; leaveSession(); const t = afterLeave; afterLeave = null; t?.(); };
$('leave-stay').onclick = () => { $('leave').hidden = true; afterLeave = null; };
addEventListener('keydown', e => {
  if (e.key !== 'Escape' || !$('panel').hidden) return;
  if (!$('leave').hidden) { e.preventDefault(); $('leave-stay').click(); return; }
  if (inSession() && !ui.briefOpen && $('tonight').hidden && document.activeElement?.id !== 'ask') { e.preventDefault(); askLeave(null); }
});
// panel.js asks before it opens a page over a session
window.knockSession = { active: inSession, ask: askLeave };

// The stairwell panel (stairwell.html) talks through this page's connection: its lobby-* asks go up, the server's lobby
// messages come back down to the frame. A reconnect joins it again.
const lobbyFrame = () => (!$('panel').hidden && $('panel-frame')?.contentWindow) || null;
addEventListener('message', e => {
  if (e.origin !== location.origin || e.source !== $('panel-frame')?.contentWindow || !/^(lobby-(join|leave|say|report)|nod-(mine|build)|logbook)$/.test(e.data?.t ?? '')) return;
  if (e.data.t === 'lobby-join') ui.inLobby = true;
  if (e.data.t === 'lobby-leave') ui.inLobby = false;
  if (e.data.t === 'nod-build') return net.send({ t: 'nod-build', day: String(e.data.day ?? ''), build: e.data.build });
  net.send({ t: e.data.t, ...(e.data.text !== undefined ? { text: String(e.data.text) } : {}), ...(e.data.id !== undefined ? { id: Number(e.data.id) } : {}) });
});
new MutationObserver(() => { if ($('panel').hidden && ui.inLobby) { ui.inLobby = false; net.send({ t: 'lobby-leave' }); } }).observe($('panel'), { attributes: true, attributeFilter: ['hidden'] });
// Neighbour of the day: the winner hears it once per win in the chat, and the builder opens by itself on the start screen
function offerBuild(m) {
  const key = 'door.nod.offered.' + m.day;
  try { if (localStorage.getItem(key)) return; localStorage.setItem(key, '1'); } catch { /* private mode: say it each visit */ }
  sys(`You were Neighbour of the day on ${m.day}, with ${m.points} points. Build a neighbour who knocks for a week.`);
  if (ui.phase === 'start' && !ui.fm && $('panel').hidden) dispatchEvent(new CustomEvent('knock-panel', { detail: '/builder' }));
}
function onMsg(m) {
  if (m.t === 'nod-offer') offerBuild(m);
  if (/^(lobby|nod)/.test(m.t ?? '') || m.t === 'logbook') { lobbyFrame()?.postMessage(m, location.origin); return; }
  // the logbook (game/cast.js): a first meeting gets one line in the chat, and the share card can show who it was
  if (m.t === 'stamp') { sys(`A stamp in your logbook: ${m.title}. You've met everyone in it.`); return; }
  if (m.t === 'met') { // the share card of this door shows the first one met at it
    sys(`New in your logbook: ${m.name}. ${m.count} of ${m.of}.`);
    if (ui.share && !ui.share.met && m.door && m.door === ui.round?.id) { ui.share.met = { key: m.key, name: m.name, count: m.count, of: m.of }; ui.share.plan = null; }
    return;
  }
  if (m.t === 'welcome' && ui.inLobby) net.send({ t: 'lobby-join' });
  switch (m.t) {
    case 'welcome': ui.playerId = m.playerId; if (m.firstMinute) setFirstMinute(true); if (!ui.joining) keepName(m); // the welcome that answers a start-screen hello must not overwrite the name being checked
 applySnapshot(m); if (tutorialDone()) net.send({ t: 'tutorial-skip' });
      if (m.name !== undefined) setTimeout(() => { tryChallenge(); openStoryLink(); }, 0); // a challenge or story link starts as soon as the start screen is up
      if (m.name !== undefined) openTonightLink(); // the welcome that answers hello (it knows about the first minute); the one at connect has no name yet
      if (m.name !== undefined && savedSession()) { ui.resuming = true; net.send({ t: 'auth-resume', session: savedSession() }); } // stay signed in (game/sessions.js)
      break;
    case 'tutorial': onTutorial(m); break;
    case 'first-minute': if (m.done) ui.fmDone = true; break;
    case 'state': applySnapshot(m); break;
    case 'online': showOnBlock(m.online); break;
    case 'auth-nonce': signNonce(m.message); break;
    case 'name-ok': keepName({ named: true, name: m.name }); nameErr(''); if (ui.joining) { const r = ui.joining; ui.joining = null; join(r); } break;
    case 'auth-ok': { const back = ui.resuming; ui.resuming = false; keepName(m); ui.wallet = m.wallet; $('wallet').textContent = short(m.wallet); $('wallet').classList.add('on'); rememberWallet(m.wallet); keepSession(m.session);
      if (!back) sys('Wallet signed in. You stay signed in on this browser for 30 days. Your points are saved to the wallet, and you can pick them up on any device by signing in again.'); break; }
    case 'auth-expired': ui.resuming = false; keepSession(null); break; // the pass is no good any more: the wallet button signs in again
    case 'error': if (ui.joining) { ui.joining = null; nameErr(m.message); $('name').focus(); $('name').select(); break; } // a refused name stays on the start screen
      if (bkui.error(m.message)) break; // the Big Knock panel answers its own refusals (the doors are shut)
      if (m.message === PLAIN_NOTICE) { refusedByServer(); break; } sys(m.message, 'err');
      if (/night shift|another tab/.test(m.message) && ui.phase === 'queued') setPhase('start');
      if (/doors are done/.test(m.message) && !LIVE.has(ui.phase)) setPhase('start'); // any phase but a live round: the dialog offers the open side
      break;
    case 'wait': onWait(m); break;
    case 'shift': setShift(m.doorsLeft, m.seconds, m.sides); break;
    case 'queued': setPhase('queued'); sys(m.role === 'doorkeeper' ? 'Waiting for a knock…' : 'Waiting for a door to answer…'); break;
    case 'round': startRound(m); break;
    case 'typing': typing(true); break;
    // a line of my own (a resumed round's history, or a demo) means I have asked: a lesson's text box folds away
    case 'msg': typing(false); line(m.text, m.from === myRole() ? 'me' : 'them'); if (m.from === myRole()) ui.asked = true; else ui.opened = true;
      ui.history.push({ from: m.from, text: m.text }); // this door's own chat: the reveal card quotes the visitor from it
      if (m.from === 'visitor') glitch.talkUntil = performance.now() + Math.min(2600, Math.max(900, String(m.text).length * 45)); // toy town's visitor talks
      if (m.from === 'doorkeeper' && ui.asking) { ui.asking = false; syncAsk(); $('ask').focus(); } break;
    case 'reveal': ui.challengeCode = m.challengeCode ?? null; if (m.story && ui.story) { ui.story.final = !!m.story.done; if (ui.story.door) ui.story.door = { ...ui.story.door, score: m.story.score }; } reveal(m); break;
    case 'story': onStory(m); break;
    case 'story-reset-ok': startStory(); break;
    case 'challenge-error': sys(m.message, 'err'); break;
    case 'tonight': onTonight(m); break;
    case 'bk-state': bkui.onState(m.state); bkui.onNext(m.next); break;
    case 'bk-called': onBigKnockCalled(m); break;
    case 'bk-reveal': bkui.onReveal(m); break;
    case 'mail': onMail(m); break;
    case 'envelope': envelope.show(m, { reduced: matchMedia('(prefers-reduced-motion: reduce)').matches, at: ENV_AT }); placeMail(); break;
    case 'mini-offer': if (!DEMO) { mini.takeOffer(m); if (ui.miniReady === m.id) mini.begin(); } break;
    case 'mini-none': if (!DEMO) { ui.miniNone = m; if (ui.miniReady === m.id) onMiniDone({ none: true, line: m.line }); } break; // a wrong guest call with no rescue ride (below the streak)
    case 'mini-seed': mini.onSeed(m); break;
    case 'mini-result': mini.onResult(m); break;
    case 'offline': sys('Connection lost. Reconnecting…', 'err'); break;
  }
}

const myRole = () => ui.role;

// ---------- chat ----------
function line(text, cls) {
  const p = document.createElement('p');
  p.className = cls; p.textContent = text;
  $('chat').appendChild(p);
  while ($('chat').children.length > 10) $('chat').firstChild.remove();
  return p;
}
const sys = (text, cls = 'sys') => line(text, cls);
let typingEl = null, typingTimer = null;
function typing(on) {
  clearTimeout(typingTimer);
  if (on && !typingEl) { typingEl = document.createElement('p'); typingEl.className = 'sys typing'; typingEl.textContent = '…'; $('chat').appendChild(typingEl); }
  if (on) typingTimer = setTimeout(() => typing(false), 5000); // the server pings every 3 s while someone types
  if (!on && typingEl) { typingEl.remove(); typingEl = null; }
}
let sentTyping = 0;
$('ask').addEventListener('input', () => { notice(''); if (ui.phase === 'round' && Date.now() - sentTyping > 3000) { sentTyping = Date.now(); net.send({ t: 'typing' }); } });
// The refusal (game/normalise.js PLAIN_NOTICE) is a notice to the sender only: it never goes into the chat or the glass.
// It clears on the next key, the next send, or after 4 seconds.
let noticeTimer = null;
function notice(text) {
  clearTimeout(noticeTimer);
  if ($('notice').textContent !== text) $('notice').textContent = text;
  if (text) noticeTimer = setTimeout(() => notice(''), 4000);
}
let lastSent = null;
const WAIT_NOTICE = 'They are about to speak.';
// true when the box may be emptied: the line was sent, or was blank, or there is no round; false when it was refused (the text stays)
function say(text) {
  const clean = plainChat(text).trim();
  if (isBlank(text) || ui.phase !== 'round') return true;
  if (!clean) { notice(PLAIN_NOTICE); return false; }
  if (ui.fm && ui.role === 'doorkeeper' && !ui.opened) { notice(WAIT_NOTICE); return false; } // the first minute: the visitor speaks first, so the chat reads in order
  if (clean.length > MAX_CHARS) { notice(LONG_NOTICE); return false; }
  notice('');
  lastSent = { el: line(clean, 'me'), text }; ui.asked = true;
  ui.history.push({ from: ui.role, text: clean });
  net.send({ t: ui.bk?.on ? 'bk-say' : 'say', text: clean });
  return true;
}
// the server refused a line this gate let through (it cannot normally happen): take the line back and hand the text to the box
function refusedByServer() {
  notice(PLAIN_NOTICE);
  if (!lastSent) return;
  lastSent.el.remove();
  if (!$('ask').value) $('ask').value = lastSent.text;
  lastSent = null;
}
$('ask').addEventListener('keydown', e => { if (e.key === 'Enter' && say(e.target.value)) e.target.value = ''; });

// ---------- shift wait: ten doors played, so the night shift waits for the next one (the scene draws the countdown) ----------
// Time left is counted from the seconds the server sends, on this device's own timer: a wrong device clock changes nothing.
function setShift(doorsLeft, seconds, sides) {
  ui.shiftDeadline = performance.now() + seconds * 1000;
  if (doorsLeft !== undefined) { ui.shiftLeft = doorsLeft; ui.shiftLeftUntil = ui.shiftDeadline; ui.shiftSides = sides; }
  syncOffer();
}
const shiftSecs = () => (ui.shiftDeadline === undefined ? null : Math.max(0, Math.ceil((ui.shiftDeadline - performance.now()) / 1000)));
function onWait(m) {
  ui.wait = m;
  ui.waitDeadline = performance.now() + m.seconds * 1000;
  setShift(0, m.seconds, m.sides);
  typing(false); $('chat').innerHTML = '';
  $('shiftwait').textContent = m.message.replace(/ Next shift in \d+:\d\d\./, ' Wait for the next shift.');
  setPhase('wait');
  if (!ONE_BIT) sys(m.message);
}
setInterval(() => {
  syncOffer(); // a shift that rolls over gives both sides back
  if (ui.shiftDeadline !== undefined && performance.now() > ui.shiftDeadline && ui.shiftTotal.target) ui.shiftTotal.reset(); // and starts the corner total again
  if (ui.phase !== 'wait' || performance.now() < ui.waitDeadline) return;
  $('shiftwait').textContent = '';
  setPhase('start');
  sys('A new shift has started.');
}, 500);

// ---------- phases ----------
// The text box and the line under it: shut until an AI doorkeeper's question is on the screen.
function syncAsk() {
  const inRound = ui.phase === 'round', visitor = inRound && ui.role === 'visitor';
  $('ask').disabled = !inRound || (visitor && !!ui.asking);
  $('wait').textContent = !visitor ? '' : ui.asking ? 'The doorkeeper is about to ask you something…' : ui.round?.aiDoorkeeper ? 'Answer the doorkeeper.' : 'Waiting for the doorkeeper…';
}
// The start dialog on the night tab offers only a side with doors left (the server refuses the other anyway).
// "Your doorkeeper doors are done for this shift: go knock." is its own line above the buttons (#sidenote).
const LIVE = new Set(['round', 'reacting']);
const liveSides = () => (ui.room === 'night' && performance.now() < (ui.shiftLeftUntil ?? 0) ? ui.shiftSides : null);
function syncOffer() {
  const offer = startOffer(liveSides()), [dk, vs] = document.querySelectorAll('#start button');
  if (dk) dk.disabled = !offer.doorkeeper;
  if (vs) vs.disabled = !offer.visitor;
  // the night shift is off: the buttons are not a dead end, they start the free room (see the start form's submit)
  if ($('sidenote') && $('sidenote').textContent !== offer.note) $('sidenote').textContent = offer.note;
}
function setPhase(phase) {
  if (phase === 'start') answerAgain('no'); // the menu instead of another door
  ui.phase = phase;
  if (phase === 'start') setTimeout(() => { tryChallenge(); openStoryLink(); }, 0);
  showRace(); // a reveal on a short phone takes the screen from the live line
  syncBrief();
  syncOffer();
  if (phase !== 'round') { sound.stop('hum'); notice(''); }
  $('start').classList.toggle('on', phase === 'start' && !ui.fm);
  $('reveal').classList.toggle('on', phase === 'reveal');
  const inRound = phase === 'round';
  syncAsk();
  const dk = inRound && ui.role === 'doorkeeper';
  $('open').disabled = !dk; $('shut').disabled = !dk;
  $('open').hidden = $('shut').hidden = ui.role === 'visitor' && inRound;
  showCoach();
  $('chips').innerHTML = '';
  if (!inRound) ui.chipPick = null;
  if (dk) for (const q of chipsFor(ui.snapshot?.tell?.id)) {
    const b = document.createElement('button'); b.textContent = q; b.onclick = () => say(q); $('chips').appendChild(b);
  }
}

// ---------- practice lessons: three guided rounds, skippable (game/tutorial.js; the server runs them) ----------
// A lesson round brings its own rule and outbreak look; the room's own come back at the next knock.
function setOutbreakLook(on) { ui.outbreak = on; document.body.classList.toggle('outbreak', on); sound.outbreak(on); }
function backToRoom() {
  ui.coach = null; ui.coachOpen = null;
  if (ui.snapshot?.tell) $('bulletin').textContent = ui.snapshot.tell.bulletin;
  setOutbreakLook(!!ui.snapshot?.outbreak);
  showCoach();
}
// the coach line: the scene draws it; the skip button shows in a lesson round, and between lessons
function showCoach() {
  const c = ui.coach, on = c && (ui.phase === 'round' || (ui.phase === 'reveal' && c.step < c.of));
  $('coach').textContent = c && ui.phase === 'round' ? c.text : '';
  $('coachskip').hidden = !on || !!c?.firstMinute; // the first minute cannot be skipped
  $('coachbar').hidden = !(c && ui.phase === 'round' && PHONE_ON) || !!c?.firstMinute; // phone: the lesson folds to one row
}
function onTutorial(m) {
  if (!m.done) return;
  rememberTutorial();
  if (!m.skipped) return; // the third lesson is over: its verdict is still on screen
  const inRound = ui.phase === 'round' && ui.coach;
  backToRoom();
  if (inRound) setPhase('start'); // the lesson round was voided
}
$('coachskip').onclick = () => net.send({ t: 'tutorial-skip' });
$('coachbar').onclick = () => { ui.coachOpen = !(ui.coachOpen ?? !ui.asked); };

// The first minute: the page shows the knock and nothing else (client/first-minute.js says what is left out)
function setFirstMinute(on) {
  ui.fm = on; document.body.classList.toggle('fm', on);
  if (!on) ui.fmDone = false;
  $('start').classList.toggle('on', ui.phase === 'start' && !ui.fm);
}
// One Key nights (roadmap 46a): the shift's key, from the round (this door) and the snapshot (the shift). Spent: Open is To shelter.
const keySpent = () => !!(ui.round?.oneKey && ui.oneKey && !ui.oneKey.held);
function syncKey() {
  const away = keySpent() && !ui.bk?.on, b = $('open');
  b.dataset.away = away ? '1' : '';
  const t = away ? 'To shelter [A]' : 'Open [A]';
  if (b.textContent !== t) b.textContent = t;
}
const ONE_KEY_NOTICE = {
  on: 'One Key night. You have one key this shift, and your one Open counts double. After that, Open becomes To shelter. A neighbour is safe there (+50), but a visitor gets in among people (-50).',
  off: 'The One Key night is over. Open works as usual again.',
};
function startRound(m) {
  $('chat').innerHTML = ''; typingEl = null; ui.opened = false;
  if (m.oneKey) ui.oneKey = m.oneKey;
  $('ask').value = ''; notice(''); // a question held for a visitor who had not spoken (say) is not carried into the next door
  if (m.coach) {
    ui.coach = m.coach; ui.coachOpen = null; ui.asked = false;
    $('bulletin').textContent = m.coach.rule; setOutbreakLook(m.coach.outbreak);
  } else if (ui.coach) backToRoom();
  ui.bk = m.bigknock ? { on: true, n: m.bigknock.n } : null;
  if (m.bigknock) { // the Big Knock's visitor: the event's rule on the notice, no outbreak, the same look for everyone (the id is the seed)
    $('bulletin').textContent = m.bigknock.rule; setOutbreakLook(false); ui.tonight = null;
    bkui.onRound();
  }
  if (m.tonight) { // tonight's door: its own rule on the notice, no outbreak, door n of five
    ui.tonight = { ...ui.tonight, on: true, final: false };
    $('bulletin').textContent = m.tonight.rule; setOutbreakLook(false);
  }
  ui.asking = !!m.asking; // an AI doorkeeper has not asked yet: the knocker waits for the question
  noteRound(m.id);
  glitchDoor(m);
  ui.round = m; syncKey(); ui.role = m.role; ui.revealKind = null; ui.revealAt = null; ui.alertAt = null; ui.revealInfo = null; ui.sum = null; delete document.documentElement.dataset.chalk;
  mail.collapse(); ui.mailMsg = null; // an envelope still on screen folds into the tray; the next knock never waits
  ui.call = null; ui.history = []; ui.card = null; $('rc').textContent = '';
  ui.tallies.seed(m.tallies ?? 0); // the server's count: a new shift, a reload and a practice round all read right
  ui.visitor = makeLook(seedOf(m.face ?? m.coach?.look ?? m.id), { outbreak: ui.outbreak, halloween: ui.halloween, animal: (m.fever || DEMO_FEVER) && !m.story && !m.coach, degen: !m.story && !m.coach }); // face: a challenge's own visitor
  if (DEMO && [...DRAFTS, ...REGULARS].includes(new URLSearchParams(location.search).get('head'))) { ui.visitor.animal = new URLSearchParams(location.search).get('head'); ui.visitor.costume = null; } // &head=goblin: a drawn neighbour at the door
  if (STORY_LOOKS[m.face]) Object.assign(ui.visitor, STORY_LOOKS[m.face]); // a story neighbour's own face
  if (m.challenge) sys(`A challenge from ${m.challenge.by}. They met this visitor. Open or keep shut?`);
  if (m.story) { // a story door: the night's rules on the door, the plain look, and the night's opening words on its first door
    ui.story = { ...ui.story, on: true, final: false, door: m.story };
    pressRoom('story'); // the bar shows the Story tab lit, and Practice or Night is the way out
    $('bulletin').textContent = m.story.rule; setOutbreakLook(false);
    if (m.story.intro) sys(`Night ${m.story.night}: ${m.story.title}. ${m.story.intro}`);
    sys(`Door ${m.story.n} of ${m.story.of}.`);
  }
  if (m.coach && !ONE_BIT) sys(m.coach.text); // the one-bit scene draws it in its own box
  if (m.tonight) sys(`Tonight, KNOCK #${m.tonight.number}: door ${m.tonight.n} of ${m.tonight.of}.`);
  if (m.bigknock) sys(`${BIGKNOCK_COPY.banner.title(m.bigknock.n)}. ${BIGKNOCK_COPY.gate}`);
  sys(m.role === 'doorkeeper' ? `Knock knock. ${m.visitorName} ${m.claim}.` : 'You\'re knocking as a curfew inspector. Pass for one of the visitors by keeping tonight\'s rule, and you score if they keep the door shut. A knock only counts after 10 seconds and 3 real lines. Begging alone ("please open, it is cold") doesn\'t count, so say something real.');
  if (m.aiDoorkeeper) sys('No human doorkeeper was free, so an AI is answering this door. Your points count half.');
  if (m.special === 'gold' && !m.coach) sys(goldLine(m.role));
  if (m.special === 'red' && !m.coach) sys("It's a red knock. It counts triple, right or wrong.", 'err');
  const litLook = m.role === 'doorkeeper' ? litOf(m.special, m.look) : null; // a bull knock in a lit knock's look (neighbours-lit.js)
  const bearLook = litLook && LIT_KNOCKS[litLook].special === 'bear'; // Mr Holden (neighbours-holden.js): the bear's rule in his look
  if (litLook && !m.coach) sys(`${LIT_KNOCKS[litLook].name} knocks. ${bearLook ? 'A wrong call costs double.' : 'Get it right for double points.'}`, bearLook ? 'err' : undefined);
  else if (m.special === 'bull' && !m.coach) sys(m.role === 'doorkeeper' ? "It's a bull knock. Get it right for double points." : 'This door is a bull knock.');
  if (m.special === 'bear' && !bearLook && !m.coach) sys(m.role === 'doorkeeper' ? "It's a bear knock. A wrong call costs double." : 'This door is a bear knock.', m.role === 'doorkeeper' ? 'err' : undefined);
  if (litLook && ui.outbreak) sys(`${LIT_KNOCKS[litLook].name} doesn't count double in an outbreak.`);
  else if ((m.special === 'bull' || m.special === 'bear') && ui.outbreak && m.role === 'doorkeeper') sys("Bull and bear knocks don't count double in an outbreak.");
  setPhase('round');
  sfx('hum');
  sound.night();
  sound.special(specialFor(m)); // a bull or bear knock gets its own file beside the generated knock
  for (const ms of [150, 600, 1050]) setTimeout(() => { if (ui.round === m && ui.phase === 'round') sfx('knock'); }, ms); // the three knocks the scene stamps
  if (m.role === 'visitor') $('ask').focus();
}

// A wrong call plays its reaction first; the verdict box comes after.
const FLAVOUR = { infected: 'One of the block\'s homes is sick now.', alien: 'One of the block\'s homes goes dark.', human: 'You left a neighbour in the cold.' };
const BB_REVEAL = { // [right call, wrong call]
  bull: ['Bull knock: you got it right, so double points.', 'Bull knock: you got it wrong, but you only lose the usual amount.'],
  bear: ['Bear knock: you got it right, for the usual points.', 'Bear knock: you got it wrong, so you lose double.'],
};
const REACT_MS = 2500; // one-bit: how long the reaction has the screen before the verdict alert
// Every call lands (call-lands.js, roadmap 60). The doorkeeper's Open or Keep shut is stamped on the glass with a ka-thunk at
// once; the server's reveal then waits out about a second of silence (HOLD_MS) before anything says how it went.
const STAMP = { open: 'opened', shut: 'kept shut', away: 'to shelter' };
function markCall(verdict) {
  if (!ONE_BIT || ui.phase !== 'round' || ui.role !== 'doorkeeper' || ui.call) return;
  const plan = landingPlan({ verdict, special: ui.round?.special, reduced: reducedMotion() });
  ui.call = { at: performance.now(), verdict, word: STAMP[verdict], plan };
  cue(HOOKS.thunk, { verdict, size: plan.size });
}
// how long after the land the verdict alert (and the reveal card) waits while the reaction plays: the freeze adds to it
const reactMsOf = () => (ui.call?.plan ? alertDelay(ui.call.plan, REACT_MS) : REACT_MS);
function reveal(m) {
  typing(false);
  announce(m); // a screen reader hears this door's verdict now, not after the glass has played (sprint 31: it read the last door's for 2.5 s)
  if (m.tonight && ui.tonight) ui.tonight.final = !!m.tonight.done; // the result screen waits for this door's reveal to be read
  // the colour look, and the older demo pages (no &ct=), land at once as before
  if (!ONE_BIT || (DEMO && DEMO_CT === null)) return land(m);
  const now = performance.now(), plan = landingPlan({ role: ui.role, verdict: m.verdict, points: m.points, kind: m.kind, special: m.special, reduced: reducedMotion() });
  // no click on this screen (a knocker's door, an AI doorkeeper's call): the bolt is heard as the reveal comes
  if (!ui.call && plan.thunk) cue(HOOKS.thunk, { verdict: m.verdict, size: plan.size });
  ui.call = { ...(ui.call ?? { at: now }), verdict: m.verdict, word: STAMP[m.verdict] ?? null, plan };
  if (DEMO_CT !== null) ui.call.at = now - DEMO_CT;
  setPhase('reacting'); // the silence: the glass held still, no verdict yet
  const c = ui.call;
  if (DEMO_CT !== null) { if (DEMO_CT >= plan.hold) land(m, DEMO_CT - plan.hold); return; }
  setTimeout(() => { if (ui.call === c) land(m); }, Math.max(0, c.at + plan.hold - now));
}
// The land: the old reveal, now after the silence. ahead0: a held demo frame that far after the land (&ct=).
function land(m, ahead0 = null) {
  const c = ui.call;
  stung = !!(c && ahead0 === null && c.plan.sting); // the landing's own sting (door:land) stands in for the plain right or wrong sound
  sfx(revealEvent(ui.role, m)); stung = false;
  sound.special(revealFileFor(m));
  if (c && ahead0 === null) {
    cue(HOOKS.land, { outcome: c.plan.outcome, sting: c.plan.sting, size: c.plan.size, kind: m.kind, verdict: m.verdict });
    if (c.plan.lunge) setTimeout(() => { if (ui.call === c) cue(HOOKS.lunge, { kind: m.kind, size: c.plan.size }); }, c.plan.impact);
  }
  const kind = reactionFor(m.kind, m.verdict);
  // a demo shows the reveal &rt= seconds in (default 2.4 s, when every reaction is well under way), in either look
  const ahead = ahead0 ?? (DEMO ? (Number(new URLSearchParams(location.search).get('rt')) || 2.4) * 1000 : 0);
  ui.revealAt = performance.now() - ahead;
  // &hold=1: the glass stays still at &rt= seconds, for a screenshot of one reveal step (neighbours-lit.js, beasts.js)
  ui.revealHold = DEMO && DEMO_CT === null && new URLSearchParams(location.search).get('hold') === '1' ? ahead : null;
  ui.revealInfo = { kind: m.kind, verdict: m.verdict };
  const react = ui.role === 'doorkeeper' && kind && !m.firstMinute && !matchMedia('(prefers-reduced-motion: reduce)').matches; // the first minute's reveal comes at once
  // when the alert and the card come up, in ms after the land: after the reaction (and its freeze), or after the lunge on a right call
  const wait = !ONE_BIT ? 0 : react ? reactMsOf() : c?.plan.lunge ? LUNGE_MS : 0;
  if (c) c.alertLand = wait;
  // One-bit: the reaction plays behind the glass (scene-onebit.js draws reactions.js poses) and the verdict alert
  // waits REACT_MS, so the eye goes to the glass first, as in the colour look; colour uses its player.
  if (ONE_BIT) {
    const flavour = ui.role === 'doorkeeper' && kind && !m.firstMinute ? FLAVOUR[kind] : '';
    // a held demo frame inside the reveal's three steps (&hold=1 with &rt= under 1 s: the steps are done by 0.8 s)
    // shows the glass alone: the verdict alert and the sum stay down so nothing but the step is in the shot
    if (ui.revealHold != null && ahead < 1000) { setPhase('reacting'); return; }
    if (!wait) { showReveal(m, flavour); return; }
    setPhase('reacting');
    if (DEMO_CT !== null && ahead < wait) return; // a held frame still inside the reaction or the lunge: the alert stays down
    const at = ui.revealAt;
    setTimeout(() => { if (ui.revealAt === at && ui.phase === 'reacting') showReveal(m, flavour); }, Math.max(0, wait - ahead));
    return;
  }
  if (react) {
    setPhase('reacting');
    const total = m.shelter?.total ?? 10, left = m.shelter?.left ?? total;
    const damaged = kind !== 'human';
    const look = kind === 'human' ? { ...ui.visitor, expression: 'worried' } : kind === 'alien' ? { ...ui.visitor, expression: 'smile' } : ui.visitor;
    player.play(kind, { look, outbreak: ui.outbreak, shelter: { left: damaged ? Math.min(total, left + 1) : left, total }, house: Math.min(total - 1, left), ahead }, {
      line: (cls, text) => (cls === 'them' ? line(text, 'them') : sys(text)),
      shake: (dx, dy) => { $('stage').style.translate = dx || dy ? `${dx}px ${dy}px` : ''; },
      done: () => showReveal(m, FLAVOUR[kind]),
    });
    return;
  }
  showReveal(m);
}

// The slip (roadmap 30): the visitor line the server named gets data-slip (style.css, scene-onebit.js draw it); the caption joins the note.
// The chat keeps ten lines, so the line is found by counting back from the newest visitor line.
function markSlip(slip) {
  $('chat').querySelectorAll('[data-slip]').forEach(e => delete e.dataset.slip);
  if (!slip) return '';
  const el = [...$('chat').children].filter(e => e.className === (ui.role === 'doorkeeper' ? 'them' : 'me')).at(-1 - slip.after);
  if (el?.textContent === slip.text) el.dataset.slip = '1';
  return slip.caption;
}

// The server's t:'mail' follows its reveal: the beats start when the reveal card has landed (showReveal), or at once if it already has.
function onMail(m) {
  if (ui.role !== 'doorkeeper') return;
  ui.mailMsg = m;
  if (ui.phase === 'reveal') playMail();
}
function playMail() {
  const m = ui.mailMsg;
  ui.mailMsg = null;
  if (!m) return;
  ui.mailTier = m.mail?.tier ?? null;
  if (ui.share && m.mail) ui.share.mail = m.mail.tier; // the share card gets the envelope line (share-card.js)
  mail.show(m, { reduced: reducedMotion(), at: MAIL_AT });
}
// the verdict as words, in the reveal panel's #rt: what a screen reader reads, and the head of the alert
const isPlayer = m => m.player === true || m.why === 'A real player.'; // the demo pages still send the old why line
function verdictParts(m) {
  const who = m.kind === 'infected' ? 'They were infected.' : m.kind === 'alien' ? 'That was a visitor.' : isPlayer(m) ? 'That was a real player.' : 'That was a neighbour.';
  const head = m.verdict === 'timeout' ? 'Time ran out.' : m.verdict === 'away' && ui.role !== 'visitor' ? `You sent them to the shelter. ${who}` : ui.role === 'visitor' ? (m.verdict === 'open' ? (m.chance ? 'The AI doorkeeper let you in this time. It opens some doors at random.' : 'They saw you were human and let you in.') : m.unreadable ? 'Not counted.' : 'You fooled them, and they kept the door shut.') : who;
  return { head, text: m.tonight ? `${head} ${m.tonight.right ? 'Right call.' : 'Wrong call.'}` : `${head} ${m.points >= 0 ? '+' : ''}${m.points}${m.mult > 1 ? ` (×${m.mult})` : ''}${m.half ? ' (half points)' : ''}` };
}
function announce(m) {
  $('rt').textContent = verdictParts(m).text;
  $('rt').style.color = m.points > 0 ? 'var(--glow)' : m.points < 0 ? 'var(--pink)' : 'var(--paper)';
}
function showReveal(m, flavour = '') {
  ui.revealKind = m.kind;
  if (!ui.houses && !m.coach && !m.firstMinute && !m.tonight && m.verdict !== 'timeout') {
    ui.houses = true; try { localStorage.setItem(HOUSES_KEY, '1'); } catch { /* private mode: shown for this visit */ }
    sys(HOUSES_LINE);
  }
  if (m.shift?.seconds !== undefined) setShift(m.shift.doorsLeft, m.shift.seconds, m.shift.sides);
  // when the one-bit alert pops (it zooms in from the button); a demo past the hold shows it settled
  ui.alertAt = Math.min(performance.now(), (ui.revealAt ?? performance.now()) + (ui.phase === 'reacting' ? ui.call?.alertLand ?? REACT_MS : 0));
  announce(m);
  const { head } = verdictParts(m);
  const litLook = litOf(m.special, ui.round?.look);
  const bb = ui.role === 'doorkeeper' && m.verdict !== 'timeout' && !m.coach
    ? (litLook ? `${LIT_KNOCKS[litLook].name}: ${BB_REVEAL[LIT_KNOCKS[litLook].special][m.points > 0 ? 0 : 1].split(': ')[1]}` : BB_REVEAL[m.special]?.[m.points > 0 ? 0 : 1]) : '';
  // the first minute's why line already quotes the slip, so its caption is left out and the alert keeps the room for the whole line
  const caption = m.firstMinute ? (markSlip(m.slip), '') : markSlip(m.slip), slipped = !m.firstMinute && !!$('chat').querySelector('[data-slip]');
  // why: false leaves out tonight's rule (m.why), for a phone alert that would otherwise hide the slipped line (scene-onebit.js)
  // after a listening-rule caption, tonight's rule is labelled so it does not read as the reason
  const rule = m.slip?.kind === 'listening' && m.why && !m.coach ? `Tonight's rule: ${m.why}` : m.why;
  // the caption without its own copy of the slipped line ("Line 2: '...'"), for a card that shows it ringed above
  const at = caption && m.slip ? caption.indexOf(m.slip.text) : -1;
  const reasonOnly = at < 0 ? caption : caption.slice(at + m.slip.text.length).replace(/^'\.?\s*/, '') || caption;
  const note = (why = true, cap = true) => [cap === 'reason' ? reasonOnly : cap ? caption : '', ui.role === 'doorkeeper' ? [flavour, why ? rule : '', bb].filter(Boolean).join(' ')
    : m.unreadable && m.verdict === 'shut' && !m.aiDoorkeeper ? "The door stayed shut, but the game couldn't find any plain English words in what you wrote. Next time, write a few real words." : '',
  m.tonight ? doorFooledLine(m.tonight.fooled) : '',
  m.half ? 'An AI doorkeeper answered, so this door only counts for half.' : '',
  ui.role === 'visitor' && m.aiDoorkeeper ? m.aiNote ?? '' : '',
  m.shift && m.shift.doorsLeft === 0 ? 'That was your last door this shift.' : lastDoorNote(m.shift?.sides, ui.role, m.shift?.doorsLeft)].filter(Boolean).join(' ');
  $('rs').textContent = note();
  const nextRole = roleToJoin(liveSides(), ui.role); // the used-up side is never joined again
  // the first minute's last reveal leads to the Pick a side menu (enter), so the button says so
  $('next').textContent = m.firstMinute?.done ? 'Pick a side' : m.story ? (m.story.done ? 'The night is over' : 'Next door') : m.tonight ?(m.tonight.done ? 'Tonight\'s result' : 'Next door') : nextRole === ui.role ? (ui.role === 'doorkeeper' ? 'Next door' : 'Next knock') : nextRole === 'visitor' ? 'Knock next' : 'Watch next';
  if (m.firstMinute?.done) ui.fmDone = true;
  ui.againDue = ui.role === 'doorkeeper' && ui.room === 'practice' && !!m.run?.end && !m.coach && !m.tonight && !m.firstMinute;
  $('aq').hidden = $('ano').hidden = !ui.againDue; // its own line and a second button; the reveal note above is left whole
  if (ui.againDue) $('next').textContent = 'Next door';
  // the reveal card (reveal-card.js): who was at the door and their best line from this door's own chat; doorkeepers only
  ui.card = ONE_BIT && ui.role === 'doorkeeper' ? cardOf({ name: ui.round?.visitorName, kind: m.kind, verdict: m.verdict, history: ui.history, slip: m.slip, player: isPlayer(m),
    rule: $('bulletin')?.textContent ?? '' }) : null; // tonight's rule as shown: the card quotes the dodge to a question about it
  ui.revealInfo = {
    ...(ui.revealInfo ?? { kind: m.kind, verdict: m.verdict }),
    view: { head, points: m.points,
      // the card (merged with the verdict) keeps the slip caption: on a block or curfew catch it is the only reason shown;
      // when the room is short, cardShort (below) drops tonight's rule instead, never the caption
      cardNote: slipped && ui.card?.quote === m.slip.text ? note() : null, again: ui.againDue, mult: m.mult, double: !!m.double, special: m.special, look: litLook, kind: ui.role === 'doorkeeper' ? m.kind : null, note: $('rs').textContent, fm: !!m.firstMinute,
      // the alert steps down so the slipped line shows above it (scene-onebit.js)
      slip: slipped ? { text: m.slip.text, short: note(false), cardShort: ui.card?.quote === m.slip.text ? note(false) : null,
        cardReason: ui.card?.quote === m.slip.text ? note(false, 'reason') : null, cardTiny: ui.card?.quote === m.slip.text ? m.slip.tiny ?? null : null } : null },
  };
  $('rc').textContent = ui.card ? cardText(ui.card) : '';
  // the card's door list: the verdict and kind the reveal just reported, a new list for each night shift (its end time names it)
  if (ui.role === 'doorkeeper' && !m.tonight) recordDoor(ui.doors, { verdict: m.verdict, kind: m.kind, name: ui.round?.visitorName }, ui.room === 'night' ? `night:${m.shift?.endsAt}` : ui.room);
  setShare(m);
  if (ui.card) { net.send({ t: CARD_EVENT }); cue(HOOKS.card, { kind: m.kind, quoteBy: ui.card.quoteBy }); }
  if (m.shelter && ui.snapshot) ui.snapshot.shelter = m.shelter;
  startSum(m);
  setPhase('reveal');
  $('next').focus();
  playMail();
}

// The share card (share-card.js, share.js): offered on the door reveal, the end of a shift, the end of the practice lessons and the
// first-minute reveal, to doorkeepers on the one-bit look (the card is made from the reveal card). Not between two lessons.
function shareScreenOf(m) {
  if (!ONE_BIT || ui.role !== 'doorkeeper' || !ui.card || m.tonight) return null; // tonight shares its own line (client/tonight.js)
  if (m.firstMinute) return 'firstMinute';
  if (m.coach) return m.coach.step >= m.coach.of ? 'practice' : null;
  return ui.room === 'night' && m.shift && m.shift.doorsLeft === 0 ? 'shift' : 'reveal';
}
function setShare(m) {
  const screen = shareScreenOf(m);
  ui.share = screen ? { screen, kind: m.kind, verdict: m.verdict, points: m.points, streak: m.tallies, quote: ui.card.quote, quoteBy: ui.card.quoteBy, doors: [...ui.doors.list], room: ui.room, plan: null } : null;
  $('share').hidden = !ui.share; $('share').textContent = 'Share';
  if (ui.share) net.send({ t: 'end-shown', screen: ui.share.screen }); // the server counts a Share click only from an end screen that was shown
}
// "Play this again?" (sprint 30 item 3): the fifth free-room door of a run ends with it. Next is yes; going back to the menu is no. The server counts one answer per door.
function answerAgain(answer) {
  if (!ui.againDue) return;
  ui.againDue = false; $('aq').hidden = $('ano').hidden = true;
  net.send({ t: 'again', mode: 'door', answer });
}
$('ano').onclick = () => { if (ui.briefOpen) return; net.send({ t: 'leave' }); setPhase('start'); }; // No: the menu, which answers no
const lastLine = o => { try { return localStorage.getItem('door.punch.' + o); } catch { return null; } };
const keepLine = (o, l) => { try { localStorage.setItem('door.punch.' + o, l); } catch { /* private mode */ } };
$('share').onclick = async () => {
  const s = ui.share, btn = $('share');
  if (!s || s.busy) return;
  s.busy = true; net.send({ t: SHARE_EVENT, screen: s.screen, ...(ui.challengeCode ? { challenge: ui.challengeCode } : {}) });
  const challengeLink = ui.challengeCode ? `${location.host}/?c=${ui.challengeCode}` : null; // the post links to this very visitor
  try {
    if (!s.plan) {
      s.plan = cardPlan({ ...s, shiftTotal: ui.room === 'night' ? ui.shiftTotal.target : undefined, name: $('name').value, host: location.host,
        last: lastLine(outcomeOf(s)) });
      keepLine(s.plan.outcome, s.plan.punchline);
    }
    await document.fonts.load('700 40px "' + SENTENCE_FONT + '"').catch(() => {});
    // sprint 34 item 3: the bright card (the Aseprite frame under the words): a phone shares the tall one, a desktop saves the feed one;
    // if the frame will not load, the old card (tall or square) goes out as before
    // a first meeting at this door shares the card with that character in its window (the bright card has no window for it)
    const bright = PHONE_ON ? BRIGHT_SIZES.story : BRIGHT_SIZES.feed, frame = !s.plan.met && await loadImage(brightFrameFile(bright)), sheet = frame && await loadImage(brightSheetFile(bright)), ok = frame && sheet;
    const size = ok ? bright : PHONE_ON ? SIZES.story : SIZES.square;
    const file = await cardFile(s.plan, size, `knock-${size.w}x${size.h}.png`, { draw: ok ? (c, p, z) => drawBrightCard(c, p, z, frame, sheet) : drawShareCard });
    const r = await shareOut({ file, text: xPostText(challengeLink ? { ...s.plan, link: challengeLink } : s.plan), phone: PHONE_ON, save: saveFile, reduced: matchMedia('(prefers-reduced-motion: reduce)').matches });
    btn.textContent = shareDone(r);
  } catch { btn.textContent = 'Share'; }
  s.busy = false;
};

// The sum on the door (sum.js): the scene draws the steps from ui.sum.at; each step gets one tick on the same clock, and
// when the last is up the shift total in the corner counts up. Reduced motion: the finished sum and one tick.
// A demo shows the sum &sumt= seconds in (default: finished).
function startSum(m) {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches, steps = sumSteps({ ...m, look: ui.round?.look }, ui.role);
  const sumt = DEMO ? new URLSearchParams(location.search).get('sumt') : null, back = DEMO ? (sumt === null ? 99 : Number(sumt) || 0) * 1000 : 0;
  const at = performance.now() - back;
  ui.sum = { steps, at, reduced, ...(sumt === null ? {} : { hold: back }) };
  ui.shiftTotal.reduced = reduced;
  const tick = () => { if (ui.sum?.at === at && ui.phase === 'reveal') sfx('tick'); };
  if (reduced) setTimeout(tick, 0);
  else if (!DEMO) steps.forEach((_, i) => { const wait = SUM_FIRST_MS + i * SUM_STEP_MS - back; if (wait >= 0) setTimeout(tick, wait); });
  const counts = ui.sum.hold !== undefined && back < sumEndMs(steps.length) ? Infinity : at + sumEndMs(steps.length); // a held demo sum has not finished
  if (Number.isInteger(m.tallies)) ui.tallies.set(m.tallies, counts, ui.sum.hold === undefined ? null : Math.max(0, back - sumEndMs(steps.length))); // the chalk changes as the sum finishes
  if (ui.room === 'night' && m.shift && Number.isFinite(m.points)) ui.shiftTotal.add(m.points, counts, String(m.shift.endsAt ?? ''));
  // a special guest's game opens when the sum has been read (the offer may come before or after this moment)
  ui.revealPoints = m.points; ui.revealShiftEnd = String(m.shift?.endsAt ?? ''); ui.miniReady = null;
  if (!DEMO && ui.role === 'doorkeeper') {
    const id = ui.round?.id;
    setTimeout(() => { if (ui.round?.id === id && ui.phase === 'reveal') { ui.miniReady = id; if (mini.pending?.id === id) mini.begin(); else if (ui.miniNone?.id === id) onMiniDone({ none: true, line: ui.miniNone.line }); } }, (reduced ? 0 : sumEndMs(steps.length)) + 700);
  }
}
// The game is over: one more chalk step on the sum (+ 35 RIDE, RESCUED + 50, or a smear), and the shift total counts the points.
function onMiniDone(m) {
  ui.miniReady = null; ui.miniNone = null;
  if (!ui.sum || ui.phase !== 'reveal' || !m) return;
  const steps = ui.sum.steps, pts = Number.isFinite(m.points) ? m.points : 0, view = ui.revealInfo?.view;
  // the chalk always ends on a total, and the card says the same number with one line that adds the door and the game up (client/mini-after.js)
  const after = afterMini({ steps, revealPoints: ui.revealPoints ?? 0, mult: view?.mult ?? 1, m }), next = after.steps;
  if (view) ui.revealInfo = { ...ui.revealInfo, view: { ...view, points: after.cardPoints, mult: 1, miniLine: after.line } };
  const head = view?.head ?? '';
  $('rt').textContent = `${head} ${after.total >= 0 ? '+' : ''}${after.total}. ${after.line}`; $('rt').style.color = after.total > 0 ? 'var(--glow)' : after.total < 0 ? 'var(--pink)' : 'var(--paper)';
  document.documentElement.dataset.chalk = next.map(s => s.text).join(' | '); // for the studio's checks: the chalk as written
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  ui.sum = { ...ui.sum, steps: next, hold: undefined, at: performance.now() - (SUM_FIRST_MS + (steps.length - 1) * SUM_STEP_MS) };
  next.slice(steps.length).forEach((_, i) => setTimeout(() => { if (ui.phase === 'reveal') sfx('tick'); }, reduced ? 0 : SUM_FIRST_MS + i * SUM_STEP_MS));
  if (ui.room === 'night' && pts > 0) ui.shiftTotal.add(pts, performance.now() + (reduced ? 0 : sumEndMs(next.length) - sumEndMs(steps.length)), ui.revealShiftEnd ?? '');
  if (ui.phase === 'reveal') $('next').focus();
}

function enter(role) {
  mini.cancel(); // a new door instead of the game: it was left
  if (ui.fmDone) { setFirstMinute(false); backToRoom(); setPhase('start'); openTonightLink(); return; } // both first-minute doors done: the menu, name and wallet appear
  if (ui.coach) backToRoom();
  ui.role = role; ui.tonight = null; ui.story = null;
  net.send({ t: 'join', room: ui.room, role });
}
// A knocker is briefed before the first knock (once; the Brief button on the knocker screen opens it again).
// One-bit: the scene draws the brief and the knock waits for "Got it". Colour look: the brief goes into the chat.
function join(role) {
  answerAgain('yes'); // Next after a run's last door
  if (ui.tonight?.on && ui.phase !== 'start') return ui.tonight.final ? showTonightEnd() : net.send({ t: 'join', room: 'practice', role: 'doorkeeper', tonight: true }); // Next on a tonight door
  if (ui.story?.on && ui.phase !== 'start') return ui.story.final ? showStoryEnd() : net.send({ t: 'join', room: 'practice', role: 'doorkeeper', story: true }); // Next on a story door
  if (role === 'visitor' && !briefSeen() && !ui.briefShown && !DEMO) { openBrief(role); if (ONE_BIT) return; }
  enter(role);
}
function openBrief(joinAfter = null) {
  ui.briefShown = true; rememberBrief();
  if (!ONE_BIT) { sys(INSPECTOR_BRIEF.title + '.'); for (const l of INSPECTOR_BRIEF.lines) sys(l); return; }
  ui.briefOpen = true; ui.briefJoin = joinAfter; syncBrief();
}
function closeBrief() {
  if (!ui.briefOpen) return;
  const role = ui.briefJoin;
  ui.briefOpen = false; ui.briefJoin = null; syncBrief();
  if (role) enter(role);
}
function syncBrief() {
  $('briefok').hidden = !(ONE_BIT && ui.briefOpen);
  $('briefbtn').hidden = !(ui.role === 'visitor' && ui.phase === 'round') || (ONE_BIT && ui.briefOpen);
}
$('briefok').onclick = closeBrief;
$('briefbtn').onclick = () => openBrief();
document.addEventListener('keydown', e => { if (e.key === 'Escape') closeBrief(); });

$('start').addEventListener('submit', e => {
  e.preventDefault();
  const role = e.submitter?.dataset.role;
  if (!role || sideUsedUp(liveSides(), role)) return; // a side used up this shift is never sent
  const typed = $('name').value.trim();
  if (NIGHT_OFF && ui.room === 'night') { sys(OFF_GO); document.querySelector('.rooms [data-room="practice"]').click(); } // coming soon: the free room, not a dead end
  net.send({ t: 'hello', name: typed || 'Guest' });
  nameErr('');
  // a changed name is checked first: the knock waits for name-ok, and a refusal is shown under the field
  if (typed && typed !== ui.savedName && !DEMO) { ui.joining = role; net.send({ t: 'set-name', name: typed }); return; }
  join(role);
});
$('name').addEventListener('input', () => nameErr(''));
function nameErr(text) { if ($('nameerr').textContent !== text) $('nameerr').textContent = text; }
// Enter in the name field joins the open side (a disabled first button would otherwise block the form)
$('name').addEventListener('keydown', e => {
  const [dk, vs] = document.querySelectorAll('#start button');
  if (e.key === 'Enter' && dk?.disabled) { e.preventDefault(); if (!vs.disabled) vs.click(); }
});
$('next').onclick = () => { if (!ui.briefOpen) join(roleToJoin(liveSides(), ui.role)); };

// ---------- Tonight (game/tonight.js, client/tonight.js) ----------
// The start screen's Tonight link: five doors, the same for everyone today. The server keeps where this browser is up to.
function startTonight() {
  if (ui.phase !== 'start' || ui.briefOpen) return;
  ui.tonight = { on: true, final: false, end: null };
  ui.room = 'practice'; ui.role = 'doorkeeper';
  document.querySelectorAll('.rooms button').forEach(x => x.setAttribute('aria-pressed', x.dataset.room === 'practice'));
  showRace();
  net.send({ t: 'hello', name: $('name').value.trim() || 'Guest' });
  net.send({ t: 'join', room: 'practice', role: 'doorkeeper', tonight: true });
}
$('tonightlink').onclick = e => { e.preventDefault(); startTonight(); };
// /?tonight=1 (the link in the share line) opens tonight's door as soon as the start screen is up: after the first minute for a fresh browser, never over a door in play
let tonightLink = wantsTonight(location.search);
function openTonightLink() { if (!tonightLink || ui.phase !== 'start' || ui.fm || ui.briefOpen || DEMO) return; tonightLink = false; startTonight(); }
function onTonight(m) {
  if (!m.end) return;
  ui.tonight = { ...ui.tonight, on: true, end: m };
  if (!ui.tonight.final) showTonightEnd(); // a finished night asked for again from the start screen: no door in between
}
function showTonightEnd() {
  const end = ui.tonight?.end;
  if (!end) return;
  const { copy } = renderTonight($('tonight'), end, { doc: document, clipboard: navigator.clipboard, send: m => net.send(m), close: closeTonight, fallback: t => selectCopy(document, t), origin: location.origin });
  $('tonight').hidden = false;
  copy.focus();
}
function closeTonight() {
  $('tonight').hidden = true;
  ui.tonight = null;
  net.send({ t: 'leave' });
  setPhase('start');
  if (ui.snapshot?.tell) $('bulletin').textContent = ui.snapshot.tell.bulletin;
}
addEventListener('keydown', e => { if (e.key === 'Escape' && !$('tonight').hidden) closeTonight(); });
// the start screen's link says where tonight stands: "Tonight #7", "Tonight #7, door 3", "Tonight #7, done"
function showTonightLink(t) {
  if (t) $('tonightlink').textContent = t.done ? `Tonight #${t.number}, done` : t.played ? `Tonight #${t.number}, door ${t.played + 1}` : `Tonight #${t.number}`;
}
// ---------- Story mode (game/story.js, client/story-view.js) ----------
// Three scripted nights. The server keeps where this browser is up to; a night's end shows its panel once its last reveal is read.
function startStory() {
  if (ui.phase !== 'start' || ui.briefOpen) return;
  ui.story = { on: true, final: false, end: null };
  ui.room = 'practice'; ui.role = 'doorkeeper'; ui.tonight = null;
  pressRoom('story');
  showRace();
  net.send({ t: 'hello', name: $('name').value.trim() || 'Guest' });
  net.send({ t: 'join', room: 'practice', role: 'doorkeeper', story: true });
}
let storyLink = !DEMO && wantsStory(location.search);
function openStoryLink() { if (!storyLink || ui.phase !== 'start' || ui.fm || ui.briefOpen) return; storyLink = false; try { history.replaceState(null, '', '/'); } catch { /* sandboxed */ } startStory(); }
function onStory(m) {
  if (!m.end) return;
  ui.story = { ...ui.story, on: true, end: m };
  if (!ui.story.final || m.again) { ui.story.final = true; showStoryEnd(); } // a finished week asked for again: its ending, no door in between
}
function showStoryEnd() {
  const end = ui.story?.end;
  if (!end) return;
  const { go } = renderStory($('tonight'), end, { doc: document, close: closeStory,
    next: () => { $('tonight').hidden = true; ui.story = { on: true, final: false, end: null }; setPhase('start'); startStory(); },
    again: () => { $('tonight').hidden = true; ui.story = null; setPhase('start'); net.send({ t: 'story-reset' }); },
    share: async text => { net.send({ t: 'share', screen: 'story' }); try { if (navigator.share) await navigator.share({ text }); else await navigator.clipboard.writeText(text); return true; } catch { return false; } } });
  $('tonight').hidden = false;
  go.focus();
}
function closeStory() {
  $('tonight').hidden = true;
  ui.story = null;
  pressRoom('practice');
  net.send({ t: 'leave' });
  setPhase('start');
  if (ui.snapshot?.tell) $('bulletin').textContent = ui.snapshot.tell.bulletin;
}

// ---------- The Big Knock (game/bigknock.js, client/bigknock.js) ----------
// A banner in the hall from 10 minutes before the doors open, a panel with the live clock and the crowd, the knock itself in the usual door
// (the server sends it as a round with `bigknock`), the wait, and the big reveal. The server's seconds are counted on this page's own clock.
// the strip sits under the menu bar: the foot of the bar's buttons (the one-bit scene draws the bar, these are its hit areas)
const bkTop = () => Math.round(Math.max(PHONE_ON ? (PHONE.menuH + 1) * PHONE.scale : 0, ...[...document.querySelectorAll('.top > button, .top > a, .top > .rooms button')].map(e => (e.getClientRects().length ? e.getBoundingClientRect().bottom : 0))));
const zoneAt = ms => { try { return new Intl.DateTimeFormat('en', { timeZoneName: 'short' }).formatToParts(new Date(ms)).find(p => p.type === 'timeZoneName')?.value ?? 'local time'; } catch { return 'local time'; } }; // "CEST", "GMT+2": the window names its zone
const bkui = createBigKnockUi({ doc: document, banner: $('bkbanner'), root: $('bigknock'), send: m => net.send(m), reduced: reducedMotion, topOf: bkTop, zoneAt,
  hall: () => ui.phase === 'start' || ui.phase === 'wait', notify: t => { if (!ui.bk?.on) sys(t); }, onBack: () => { if (ui.phase !== 'round') setPhase('start'); }, onShare: m => shareBigKnock(m),
  startLine: t => { $('bknext').textContent = t; }, visitor: (box, o) => mountVisitor(box, o), set: (fn, ms) => setTimeout(fn, ms), clear: id => clearTimeout(id) });
setInterval(() => bkui.tick(), 1000);
function onBigKnockCalled(m) {
  if (ui.bk) ui.bk.on = false;
  if (ui.phase === 'round' || ui.phase === 'reacting') setPhase('start'); // the call is in; the door is not shown again
  bkui.onCalled(m);
}
async function shareBigKnock(m) {
  const btn = document.querySelector('#bigknock .bk-share');
  const outcome = m.you?.outcome === 'winner' ? 'winner' : m.you?.outcome === 'right' ? 'right' : m.you?.outcome === 'fooled' ? 'fooled' : null;
  if (!outcome || btn?.dataset.busy) return;
  if (btn) btn.dataset.busy = '1';
  net.send({ t: SHARE_EVENT, screen: 'bigknock' });
  const label = text => { const t = btn?.querySelector('.t'); if (t) t.textContent = text; };
  try {
    const plan = cardPlan({ screen: 'bigknock', bigknock: { n: m.n, outcome, right: m.right, real: m.real, kind: m.kind }, name: ui.savedName ?? '', host: location.host });
    await document.fonts.load('700 40px "' + SENTENCE_FONT + '"').catch(() => {});
    const size = PHONE_ON ? SIZES.story : SIZES.square;
    const file = await cardFile(plan, size, `knock-big-${m.n}-${size.w}x${size.h}.png`, { draw: drawShareCard });
    const r = await shareOut({ file, text: xPostText(plan), phone: PHONE_ON, save: saveFile, reduced: reducedMotion() });
    label(shareDone(r));
  } catch { label('Share'); }
  if (btn) delete btn.dataset.busy;
}

$('open').onclick = () => {
  const v = keySpent() && !ui.bk?.on ? 'away' : 'open';
  markCall(v); net.send({ t: ui.bk?.on ? 'bk-decide' : 'decide', verdict: v });
};
$('shut').onclick = () => { markCall('shut'); net.send({ t: ui.bk?.on ? 'bk-decide' : 'decide', verdict: 'shut' }); };
document.addEventListener('keydown', e => {
  if (e.target.tagName === 'INPUT' || ui.phase !== 'round' || ui.role !== 'doorkeeper' || !$('panel').hidden) return;
  if (e.key === 'a' || e.key === 'A') $('open').click();
  if (e.key === 'd' || e.key === 'D') $('shut').click();
});
const pressRoom = room => document.querySelectorAll('.rooms button').forEach(x => x.setAttribute('aria-pressed', x.dataset.room === room));
document.querySelectorAll('.rooms button').forEach(b => b.onclick = () => {
  // the story is a room of its own on the bar: it starts the week where this browser left it (a door in play asks first)
  if (b.dataset.room === 'story') {
    if (ui.story?.on) return; // already in the story
    const go = () => { setPhase('start'); startStory(); };
    return inSession() ? askLeave(go) : go();
  }
  // any other room: a door in play, or a story or Tonight night, asks "Step away from the door?" first, so there is always a way out
  const go = () => {
    ui.room = b.dataset.room; ui.tonight = null; ui.story = null;
    pressRoom(b.dataset.room);
    net.send({ t: 'leave' });
    $('tonight').hidden = true;
    setPhase('start');
    showRace();
    if (ui.snapshot?.tell) $('bulletin').textContent = ui.snapshot.tell.bulletin;
    if (ui.room === 'night' && NIGHT_OFF) sys(OFF_SHORT);
    else if (ui.room === 'night' && !ui.wallet) sys('The night shift needs a wallet that holds KNOCK. Connect one first.');
  };
  return inSession() ? askLeave(go) : go();
});

// ---------- wallet (Phantom on devnet) ----------
// Staying signed in (game/sessions.js): the server's pass is kept in this browser and sent on each visit. Signed in, the
// wallet button signs out on a second tap within 5 s.
const SESSION_KEY = 'door.session';
function savedSession() { try { return localStorage.getItem(SESSION_KEY); } catch { return null; } }
function keepSession(pass) { try { if (pass) localStorage.setItem(SESSION_KEY, pass); else localStorage.removeItem(SESSION_KEY); } catch { /* private mode */ } }
let signOutAsk = 0;
$('wallet').onclick = async () => {
  if (ui.wallet) {
    if (performance.now() - signOutAsk < 5000) { keepSession(null); try { localStorage.removeItem('door.wallet'); } catch { /* private mode */ } location.reload(); return; }
    signOutAsk = performance.now();
    return sys(`Signed in as ${short(ui.wallet)}. Tap the wallet again to sign out of this browser.`);
  }
  const provider = window.phantom?.solana ?? window.solana;
  if (!provider) return sys('No Solana wallet found. Install Phantom first.', 'err');
  try {
    const res = await provider.connect();
    net.send({ t: 'auth-start', wallet: res.publicKey.toBase58() });
  } catch { sys('Wallet connection was cancelled.', 'err'); }
};
// The leaderboards page reads it to find you by wallet (client/leaders.js).
function rememberWallet(wallet) { try { localStorage.setItem('door.wallet', wallet); } catch { /* private mode */ } }
async function signNonce(message) {
  const provider = window.phantom?.solana ?? window.solana;
  try {
    const { signature } = await provider.signMessage(new TextEncoder().encode(message), 'utf8');
    net.send({ t: 'auth', wallet: provider.publicKey.toBase58(), signature: base58(signature), message });
  } catch { sys('Signing was cancelled.', 'err'); }
}
const ALPHA = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
function base58(bytes) {
  let n = 0n; for (const b of bytes) n = n * 256n + BigInt(b);
  let s = ''; while (n > 0n) { s = ALPHA[Number(n % 58n)] + s; n /= 58n; }
  for (const b of bytes) { if (b !== 0) break; s = '1' + s; }
  return s;
}
const short = w => (w ? `${w.slice(0, 4)}…${w.slice(-4)}` : '');

// ---------- room data ----------
// "N people on the block now" on the start screen: the server's count of real connections, empty at 0 (#onblock).
const showOnBlock = n => { if ($('onblock')) $('onblock').textContent = onBlockLine(n); };
function applySnapshot(s) {
  if (s.online !== undefined) showOnBlock(s.online);
  if (s.shift?.seconds !== undefined) ui.shiftDeadline = performance.now() + s.shift.seconds * 1000;
  if (Number.isInteger(s.shift?.sides?.doorkeeper?.used)) { ui.shiftSides = s.shift.sides; ui.shiftLeftUntil = ui.shiftDeadline; }
  showTonightLink(s.tonight);
  if (s.bigknock !== undefined) bkui.onState(s.bigknock);
  if (s.bigknockNext !== undefined) bkui.onNext(s.bigknockNext);
  const lesson = (ui.coach || ui.tonight?.on || ui.story?.on) && ui.phase !== 'start'; // a lesson round, or a tonight door, has its own rule and outbreak look
  if (s.oneKey !== undefined && !lesson) {
    if (!!s.oneKey !== !!ui.oneKey && (s.oneKey || ui.oneKeySaid)) { sys(s.oneKey ? ONE_KEY_NOTICE.on : ONE_KEY_NOTICE.off, s.oneKey ? 'err' : 'sys'); ui.oneKeySaid = !!s.oneKey; }
    ui.oneKey = s.oneKey;
    syncKey();
  }
  if (!lesson && s.outbreak !== undefined && s.outbreak !== ui.outbreak) {
    ui.outbreak = s.outbreak;
    document.body.classList.toggle('outbreak', s.outbreak);
    sound.outbreak(s.outbreak);
    if (s.outbreak) sfx('siren');
    sys(s.outbreak ? OUTBREAK_NOTICE.on : OUTBREAK_NOTICE.off, s.outbreak ? 'err' : 'sys');
  }
  if (s.halloween !== undefined && s.halloween !== ui.halloween) {
    ui.halloween = s.halloween;
    document.body.classList.toggle('halloween', s.halloween); // CSS hook: orange #f08a2c only under this class
    sys(s.halloween ? HALLOWEEN_LINES.on : HALLOWEEN_LINES.off);
  }
  const flatsWere = ui.snapshot?.shelter?.left;
  if (ui.houses && s.shelter && flatsWere !== undefined && s.shelter.left < flatsWere) sys(flatsLostLine(s.shelter));
  ui.snapshot = { ...ui.snapshot, ...s };
  showRace();
  if (s.tell && !lesson) $('bulletin').textContent = s.tell.bulletin;
  if (s.leaderboard) {
    const mine = mineIndex(s.leaderboard, ui.playerId, ui.wallet, ui.savedName);
    if (ui.room === 'night' && mine >= 0) ui.shiftTotal.seed(s.leaderboard[mine].points); // a reload: the corner starts from the board
    const rows = s.leaderboard.slice(0, 3).map((r, i) => [r.name || short(r.wallet), r.points, i === mine]);
    if (mine >= 3) rows.push([`you (${mine + 1}th)`, s.leaderboard[mine].points, true]);
    // the free room keeps a running score and streak for the session, across shifts (sprint 30 item 3): first, so the small board shows them
    if (ui.room === 'practice' && s.session && (s.session.points || s.session.streak)) {
      rows.unshift(['Your session', s.session.points, true]);
    }
    $('board').replaceChildren(...rows.map(([n, p, me]) => {
      const d = document.createElement('div'); if (me) d.className = 'you';
      d.append(Object.assign(document.createElement('span'), { textContent: n }), Object.assign(document.createElement('span'), { textContent: p }));
      return d;
    }));
    if (!rows.length) $('board').textContent = 'No scores this shift yet.';
  }
  if (s.ledger && !NIGHT_OFF) { // night shift off: #receipt, #jar and #footStats stay empty (no amounts, nothing for a screen reader to read)
    $('receipt').replaceChildren(...s.ledger.slice(0, 3).map(l => {
      const a = document.createElement('a');
      a.href = l.sig ? `https://explorer.solana.com/tx/${l.sig}?cluster=devnet` : '#';
      a.target = '_blank'; a.rel = 'noopener';
      const time = new Date(l.at).toTimeString().slice(0, 5);
      a.append(Object.assign(document.createElement('span'), { textContent: `${time} ${ledgerLabel(l)}` }),
        Object.assign(document.createElement('span'), { textContent: (Number(l.lamports) / 1e9).toFixed(3) }));
      return a;
    }));
    if (!s.ledger.length) $('receipt').textContent = 'No payouts yet.';
  }
  if (s.prizePoolLamports !== undefined && !NIGHT_OFF) {
    const sol = Number(s.prizePoolLamports) / 1e9;
    $('jar').textContent = `${sol.toFixed(2)} SOL this shift`;
    ui.coins = Math.ceil(sol * 3);
  }
  $('footStats').textContent = s.prizePct && !NIGHT_OFF ? `The top 10 of each shift share ${s.prizePct}% of the creator fees when the shift ends.` : '';
}


// ---------- look setting (remembered per viewer in localStorage) ----------
// three stops: night colour (the one-bit look's door in level 2 colour), one bit, the old colour version
$('look').textContent = choiceLabel(CHOICE);
const onPhone = () => isPhone(...viewSize());
$('look').setAttribute('aria-label', `${choiceLabel(CHOICE)}. Switch to ${choiceLabel(nextChoice(CHOICE, onPhone())).slice(6)}.`);
$('look').onclick = () => {
  if (ui.phase === 'round') return sys('Finish this knock first.', 'err');
  const { look, door } = choiceSaves(nextChoice(CHOICE, onPhone()));
  saveLook(look); if (door) saveDoor(door);
  const u = new URL(location.href); u.searchParams.delete('look'); u.searchParams.delete('door'); location.href = u.href;
};

// ---------- phone menu: the door icon pulls down wallet, sound, look and roadmap (scene-onebit.js draws it) ----------
function setMenu(open) { $('menu').setAttribute('aria-expanded', String(open)); }
$('menu').onclick = () => setMenu($('menu').getAttribute('aria-expanded') !== 'true');
const MENU_ITEMS = ['wallet', 'mute', 'look', 'roadmap', 'leaders', 'logbook', 'stairwell', 'report'];
const MENU_TAPS = [...MENU_ITEMS, 'roompick', 'coincopy', 'discord']; // and the pull-down's way to the rooms (a link: the tap goes there and the menu shuts), the $KNOCK Copy and Discord
// a tap on an item does its job and closes the menu; a tap anywhere else just closes it
for (const id of MENU_TAPS) $(id).addEventListener('click', () => setMenu(false));
addEventListener('pointerdown', e => {
  if ($('menu').getAttribute('aria-expanded') === 'true' && !e.target.closest?.('#menu, ' + MENU_TAPS.map(i => '#' + i).join(', '))) setMenu(false);
});
addEventListener('keydown', e => { if (e.key === 'Escape') { setMenu(false); setRule(false); } });
// a short phone folds tonight's rule into a one-line strip (phone.js); a tap opens it over the glass, another shuts it
function setRule(open) { $('rulefold').setAttribute('aria-expanded', String(open)); }
$('rulefold').onclick = () => setRule($('rulefold').getAttribute('aria-expanded') !== 'true');

// ---------- the $KNOCK field (client/coin.js): Copy is there only once the server filled in a real address ----------
const coinMint = $('coin')?.dataset.mint;
if (coinMint) $('coincopy').onclick = async () => {
  let done = false;
  try { await navigator.clipboard.writeText(coinMint); done = true; } catch { /* no clipboard permission: the old way below */ }
  if (!done) {
    const t = Object.assign(document.createElement('textarea'), { value: coinMint }); t.style.position = 'fixed'; t.style.opacity = '0';
    document.body.append(t); t.select();
    try { done = document.execCommand('copy'); } catch { /* not copied */ }
    t.remove();
  }
  if (!done) return sys("That didn't copy. Please try again.", 'err');
  $('coincopy').textContent = COPIED_LABEL;
  sys('The $KNOCK contract address is copied.');
  setTimeout(() => { $('coincopy').textContent = COPY_LABEL; }, 1500);
};

// ---------- the animals (game/animals.js): mixed into the lineup by seed, and now and then a Full fever run the server announces (m.fever).
// The old top-bar switch is gone. Its stored key, door.fever, is no longer read or written, so an old value does nothing.

// ---------- sound setting (remembered per viewer in localStorage) ----------
const showMute = () => { $('mute').textContent = muteLabel(sound.muted); $('mute').setAttribute('aria-pressed', sound.muted); };
$('mute').onclick = () => { sound.toggle(); showMute(); };
showMute();

// ---------- render loop + scaling ----------
if (ONE_BIT) $('stage').prepend(document.querySelector('.top')); // its buttons become hit areas on the menu bar
// one-bit: sentences go on their own canvas over the scene, sharp at screen resolution (words.js)
const wordsCanvas = ONE_BIT ? Object.assign(document.createElement('canvas'), { id: 'words' }) : null;
if (wordsCanvas) { wordsCanvas.setAttribute('aria-hidden', 'true'); $('wrap').append(wordsCanvas); }
const words = wordsCanvas ? createWords(wordsCanvas) : null;
if (words && NIGHT_DOOR) words.colours(NIGHT.ink, NIGHT.paper);
let SCALE = 1;
const scene = ONE_BIT ? createOneBitScene($('c'), document, words, { night: NIGHT_DOOR }) : createScene($('c').getContext('2d'));
const player = ONE_BIT ? null : createReactionPlayer($('c').getContext('2d'), scene);
const clockNow = () => { $('clock').textContent = new Date().toTimeString().slice(0, 5); };
setInterval(() => { ui.flicker = Math.random() < 0.06 ? 0.45 : 1; }, 140);
setInterval(clockNow, 1000); clockNow();
let lastFrame = -1;
function frame() {
  if (ONE_BIT) { // stepped motion: redraw only on a new 15 fps tick
    const now = performance.now(), f = Math.floor(now / 1000 * FPS);
    if (f !== lastFrame) {
      lastFrame = f;
      const s = ui.snapshot ?? {};
      // the call (call-lands.js): its moment, held still on a demo's &ct=; a freeze keeps the frame it froze on, the flash and
      // the shake play over it (the shake moves the whole stage, words and all, whole logical pixels at a time)
      const c = ui.call, cf = c ? callFrame(c.plan, DEMO_CT ?? now - c.at) : null;
      if (cf?.frozen) c.fz ??= { now, wall: Date.now() }; else if (c) c.fz = null;
      const sNow = c?.fz ? c.fz.now : now, sWall = c?.fz ? c.fz.wall : Date.now();
      const [shx, shy] = cf && PHONE_ON && cf.art ? phoneShake(cf.shake, Math.floor(viewSize()[0] / SCALE)) : cf?.shake ?? [0, 0]; // the punched glass keeps its palms in the window
      const sh = shx || shy ? `${shx * SCALE}px ${shy * SCALE}px` : '';
      if ($('wrap').style.translate !== sh) $('wrap').style.translate = sh;
      const landed = cf && cf.glass >= 0 && ui.revealAt != null;
      const gl = toyLevel(now, glitch), toyText = gl >= 0.5;
      scene.setGlitch?.(gl > 0 ? { level: gl, variant: glitch.variant, now, reduced: reducedMotion(), talking: now < glitch.talkUntil } : null);
      if (toyText !== !!ui.toyText) { // toy town's navy text on white, then back to the door's own
        ui.toyText = toyText; document.documentElement.classList.toggle('toy', toyText);
        if (toyText) words.colours(TOY.ink, TOY.paper); else if (NIGHT_DOOR) words.colours(NIGHT.ink, NIGHT.paper); else words.colours();
      }
      scene.draw({
        call: cf ? { ...cf, word: c.word } : null, card: ui.phase === 'reveal' ? ui.card : null, cardReduced: reducedMotion(), reduced: reducedMotion(),
        viewRows: PHONE_ON ? Math.floor(viewSize()[1] / SCALE) : null, // the window's height in scene rows: the phone's punch-in fills 60% of it
        now: sNow, wall: sWall, phase: ui.phase, role: ui.role, round: ui.round, visitor: ui.visitor, coachStep: ui.coach?.step ?? 0, coachOf: ui.coach?.of ?? 0,
        special: ui.round?.special ?? null, look: ui.round?.look ?? null, outbreak: ui.outbreak, oneKey: ui.oneKey ?? null, story: ui.story?.on ? ui.story.door ?? null : null, halloween: ui.halloween, shelter: s.shelter, houses: ui.houses && !ui.story?.on, flicker: ui.flicker,
        brief: ui.briefOpen ? INSPECTOR_BRIEF : null,
        fm: ui.fm, coach: ui.phase === 'round' ? ui.coach : null, coachBetween: ui.phase !== 'round' ? ui.coach : null, coachOpen: ui.coachOpen ?? !ui.asked,
        wait: ui.phase === 'wait' ? { ...ui.wait, left: Math.max(0, Math.ceil((ui.waitDeadline - now) / 1000)), sidesLine: ui.wait?.sides ? sidesLine(ui.wait.sides) : null } : null,
        sidesLine: ui.room === 'night' && now < (ui.shiftLeftUntil ?? 0) && ui.shiftSides?.doorkeeper?.used !== undefined ? sidesLine(ui.shiftSides) : null,
        shiftTitle: shiftTitle(ui.room === 'night', now < (ui.shiftLeftUntil ?? 0) ? ui.shiftLeft : null, shiftSecs(), undefined, now < (ui.shiftLeftUntil ?? 0) ? ui.shiftSides : null),
        shiftTitleShort: shiftTitleShort(ui.room === 'night', now < (ui.shiftLeftUntil ?? 0) ? ui.shiftLeft : null, shiftSecs(), undefined, now < (ui.shiftLeftUntil ?? 0) ? ui.shiftSides : null), revealAt: landed ? sNow - cf.glass : ui.revealHold != null && ui.revealAt != null ? now - ui.revealHold : ui.revealAt,
        alertAt: DEMO_CT !== null && c?.alertLand != null && ui.alertAt != null ? sNow - (DEMO_CT - c.plan.hold - c.alertLand) : ui.alertAt, reveal: ui.revealInfo,
        sum: ui.phase === 'reveal' ? ui.sum : null, shiftTotal: ui.room === 'night' ? ui.shiftTotal.value(now) : null,
        tallies: ui.round?.tallies !== undefined && (ui.phase === 'round' || ui.phase === 'reacting' || ui.phase === 'reveal') ? ui.tallies.view(now) : null, prizePoolLamports: s.prizePoolLamports, prizePct: s.prizePct,
      });
      if (PHONE_ON) words.render(SCALE, devicePixelRatio || 1, ...scene.size());
      else words.render(SCALE, devicePixelRatio || 1, W1, scene.size()[1]);
    }
    requestAnimationFrame(frame); return;
  }
  if (player.active) { player.draw(performance.now()); requestAnimationFrame(frame); return; }
  const r = ui.round, roundOn = ui.phase === 'round' && r;
  scene.draw({
    role: ui.role, flicker: ui.flicker,
    visitor: ui.phase === 'round' || ui.phase === 'reveal' ? ui.visitor : null,
    reveal: ui.phase === 'reveal' ? ui.revealKind : null,
    outbreak: ui.outbreak, halloween: ui.halloween,
    special: ui.round?.special ?? null,
    timerFrac: roundOn ? (r.endsAt - Date.now()) / (roundSeconds(r) * 1000) : 0,
    shelter: ui.snapshot?.shelter, coins: ui.coins,
  });
  requestAnimationFrame(frame);
}
// Phone (one-bit, portrait, under 640 px wide; phone.js): its own layout at 2x, as wide as the window. The strip
// keeps the disclaimer in view and the page scrolls down to "This shift". Landscape phones keep the desktop layout.
let PHONE_ON = false;
function fitPhone() {
  const root = document.documentElement;
  $('strip').hidden = false; root.classList.add('strip');
  // the live line (roadmap 29) sits fixed just above the disclaimer strip, so the play area leaves room for both
  const race = $('race'); race.style.bottom = `${$('strip').offsetHeight}px`;
  const [vw, vh] = viewSize(), strip = $('strip').offsetHeight + (race.hidden ? 0 : race.offsetHeight), { w, h } = phoneSize(vw, vh, strip);
  scene.phone(w, h);
  const [sw, sh] = scene.size();
  SCALE = PHONE.scale;
  root.style.setProperty('--w', `${sw * SCALE}px`);
  root.style.setProperty('--h', `${sh * SCALE}px`);
  Object.assign($('stage').style, { width: `${sw}px`, height: `${sh}px`, transform: `scale(${SCALE})` });
  $('wrap').style.marginBottom = `${strip}px`;
}
function fit() {
  const phone = ONE_BIT && isPhone(...viewSize());
  if (phone !== PHONE_ON) {
    PHONE_ON = phone;
    document.documentElement.classList.toggle('portrait', phone);
    $('menu').hidden = !phone; setMenu(false);
    $('rulefold').hidden = !phone; setRule(false); showCoach();
    if (!phone) { $('stage').style.width = $('stage').style.height = ''; $('wrap').style.marginBottom = ''; }
  }
  if (phone) return fitPhone();
  // integer scale only; one-bit draws its own menu bar, colour leaves room for the top and foot bars
  const [w, h, margin] = ONE_BIT ? [W1, H1, 0] : [W, H, 90];
  const s = SCALE = ONE_BIT ? oneBitScale(innerWidth, innerHeight) : Math.max(1, Math.floor(Math.min(innerWidth / w, (innerHeight - margin) / h)));
  if (ONE_BIT) {
    const on = needsStrip(innerWidth, innerHeight); $('strip').hidden = !on; document.documentElement.classList.toggle('strip', on);
    const rows = oneBitHeight(innerWidth, innerHeight); // and the floor runs to the bottom edge: no black band (sprint 10)
    if (rows > H1) scene.tall(oneBitWidth(innerWidth, innerHeight), rows);
    else scene.resize(oneBitWidth(innerWidth, innerHeight)); // the room runs to the right edge: no black strip (sprint 6)
  }
  document.documentElement.style.setProperty('--w', `${w * s}px`);
  document.documentElement.style.setProperty('--h', `${(ONE_BIT ? scene.size()[1] : h) * s}px`);
  $('stage').style.transform = `scale(${s})`;
  if (ONE_BIT) placeRace(s);
  if (envelope.open) placeMail();
}
// The envelope (roadmap 29) on the one-bit desktop: under the door's centre, out from under the threshold, and the letter
// beside the door when there is room (mailSpot in envelope.js). Phone and colour look: the envelope's own bottom-centre place.
// Measured while the envelope is open, so the disclaimer strip it forces into view counts.
function placeMail() {
  const el = $('envelope');
  if (!ONE_BIT || PHONE_ON) { el.removeAttribute('data-at'); el.removeAttribute('data-paper'); return; }
  const r = $('stage').getBoundingClientRect();
  const m = mailSpot({ stage: { left: r.left, top: r.top }, scale: SCALE, view: { w: innerWidth, h: innerHeight }, bottom: $('strip').offsetHeight });
  el.setAttribute('data-at', m.at);
  for (const [k, v] of [['--env-x', m.x], ['--env-top', m.top], ['--env-px', m.px], ['--env-paper-left', m.paperLeft ?? 0]]) el.style.setProperty(k, `${v}px`);
  if (m.paperLeft === null) el.removeAttribute('data-paper'); else el.setAttribute('data-paper', '');
}
// Desktop one-bit: the live line lies on the floor under the door, between the shift total (bottom left) and
// "This shift", its bottom edge on the bottom of the 640x360 layout (RACE_SLOT, logical pixels). Colour look: in the flow.
const RACE_SLOT = { left: 78, right: 294, bottom: 358 };
function placeRace(s) {
  const r = $('stage').getBoundingClientRect(), race = $('race');
  Object.assign(race.style, { left: `${Math.round(r.left + scrollX + RACE_SLOT.left * s)}px`, width: `${(RACE_SLOT.right - RACE_SLOT.left) * s}px`,
    top: `${Math.round(r.top + scrollY + RACE_SLOT.bottom * s)}px`, bottom: '' });
}
addEventListener('resize', fit); fit();
$('name').value = sessionStorage.getItem('door.name') ?? '';
setPhase('start');
if (DEMO) {
  const q = new URLSearchParams(location.search); // &halloween=on (&costume=mask|sheet|pumpkin, &tell=candy): the edition, for screenshots
  if (/^shift-|^sum-|^tally-|^envelope|^mail-|^slip$|^slip-knocker$/.test(DEMO)) document.querySelector('.rooms [data-room="night"]').click(); // a refused player is on the night tab
  if (q.get('mute') === '1') { sound.preview(true); showMute(); } // &mute=1: the muted button for screenshots, not saved
  for (const m of demoScript(DEMO, Date.now(), { halloween: q.get('halloween') === 'on', costume: q.get('costume'), animal: q.get('animal'), tell: q.get('tell'), left: Number(q.get('left')) || 0, fail: q.get('fail') })) onMsg(m);
  if (DEMO === 'tonight-end') { // sprint 29 item 2: the result screen with sample numbers (screenshots only)
    const results = [true, true, false, true, true];
    ui.tonight = { on: true, final: false };
    onTonight({ t: 'tonight', end: true, day: '2026-10-20', number: 7, results, right: 4, of: 5, line: 'KNOCK #7 \u{1F7E9}\u{1F7E9}\u{1F7E5}\u{1F7E9}\u{1F7E9} 4/5',
      fooled: { pct: 36, of: 25 }, doors: [{ pct: 8, of: 25 }, { pct: 20, of: 25 }, { pct: 64, of: 25 }, { pct: 40, of: 25 }, { pct: 48, of: 25 }], streak: 3 });
  }
  if (/^bigknock-/.test(DEMO)) { // sprint 29 item 3: the event's screens with sample numbers (screenshots only)
    const base = { n: 7, phase: 'open', seconds: { toOpen: 0, toClose: 432, toReveal: 552 }, crowd: 1031, calls: 412, joined: false, called: false };
    const nextWin = { n: 8, ...windowOf(Date.now() - (Date.now() % 60000)) }; // the public window for the next event, from now (screenshots only)
    const you = { winner: { outcome: 'winner', kind: 'big', points: 150, cosmetic: 'big' }, right: { outcome: 'right', kind: 'points', points: 20 },
      fooled: { outcome: 'fooled', kind: 'fragments', fragments: 3, keyring: { have: 7, of: 10 } }, notcounted: { outcome: 'notCounted' } };
    const [, what, who] = DEMO.split('-');
    bkui.onNext(nextWin);
    if (what === 'banner') bkui.onState({ ...base, phase: 'announced', seconds: { toOpen: 600, toClose: 1200, toReveal: 1320 }, crowd: 0 });
    else if (what === 'panel') { bkui.onState(base); bkui.open(); }
    else if (what === 'soon') { bkui.onState({ ...base, phase: 'announced', seconds: { toOpen: 600, toClose: 1200, toReveal: 1320 }, crowd: 0 }); bkui.open(); }
    else if (what === 'wait') { bkui.onState({ ...base, phase: 'deciding', seconds: { toOpen: 0, toClose: 0, toReveal: 108 }, joined: true, called: true }); bkui.onCalled({ t: 'bk-called', counts: true }); }
    else if (what === 'waitopen') { bkui.onState({ ...base, joined: true, called: true }); bkui.onCalled({ t: 'bk-called', counts: true }); }
    else if (what === 'quiet') { /* nothing announced: the start dialog carries the window line */ }
    else if (what === 'strip') { onMsg({ t: 'round', id: 'bk-7', role: 'doorkeeper', visitorName: 'Ada', claim: 'is at the door', endsAt: Date.now() + 60000, seconds: 60, special: null }); bkui.onReveal({ t: 'bk-reveal', n: 7, kind: 'alien', joined: 1100, real: 1031, right: 412, winners: 10, hash: 'x', you: you.winner, next: nextWin }); }
    else if (what === 'round') {
      onMsg({ t: 'round', id: 'bk-7', role: 'doorkeeper', visitorName: 'Big Knock #7', claim: 'is at the door', endsAt: Date.now() + 120000, seconds: 120, special: null,
        bigknock: { n: 7, rule: 'Visitors never name a colour, like red or blue.' } });
      onMsg({ t: 'msg', from: 'visitor', text: 'Hi, sorry about the hour.' });
    } else if (what === 'reveal') bkui.onReveal({ t: 'bk-reveal', n: 7, kind: q.get('kind') === 'human' ? 'human' : 'alien', joined: 1100, real: 1031, right: 412, winners: 10, hash: 'x', you: you[who] ?? null, next: nextWin });
  }
  if (DEMO === 'brief' || DEMO === 'brief-round') { if (ONE_BIT) { ui.briefOpen = true; syncBrief(); } else openBrief(); } // the inspector brief, open
}
const PIXEL_FONTS = ['20px "Jersey 10"', '15px "Jersey 15"', '8px "Tiny5"', `16px "${SENTENCE_FONT}"`, `700 16px "${SENTENCE_FONT}"`];
(ONE_BIT ? Promise.all(PIXEL_FONTS.map(f => document.fonts.load(f))).catch(() => {}) : document.fonts.ready).then(frame);
